import React, { useEffect, useMemo, useRef, useState } from "react";
import { Helmet } from "react-helmet";
import { useNavigate } from "react-router-dom";
import api from "../utils/api";
import ReviewsBar from "../components/ReviewsBar";
import LiveStrip from "../components/LiveStrip";
import LegolandLiveSection from "../components/legoland/LegolandLiveSection";
import "../styles/parkingVsTaxi.css";

const DEST_POSTCODE = "SL4 4AY";
const PARK_COORDS = { lat: 51.462807, lon: -0.647736 };
const LEGOLAND_BUFFET_MERLIN = {
  adult: 16.75,
  child: 10,
};
const LEGOLAND_BUS_ROWS = [
  {
    from: "Premier Inn Slough Central South (Windsor Rd)",
    to: "LEGOLAND",
    bus: "702 / 703",
    fromStop: "Landmark Place (Stop S) · 3 min walk",
    toStop: "LEGOLAND Park & Ride (Stop LP)",
    duration: "35–43 mins",
    frequency: "Every 5–31 mins",
    fare: "£3 / £6 return",
  },
  {
    from: "Travelodge Slough (Herschel St)",
    to: "LEGOLAND",
    bus: "702 / 703",
    fromStop: "Landmark Place (Stop S) · 2 min walk",
    toStop: "LEGOLAND Park & Ride (Stop LP)",
    duration: "34–38 mins",
    frequency: "Every 5–31 mins",
    fare: "£3 / £6 return",
  },
  {
    from: "Copthorne Hotel Slough-Windsor",
    to: "LEGOLAND",
    bus: "702 / 703",
    fromStop: "Albert Street · 25 min walk",
    toStop: "LEGOLAND Park & Ride (Stop LP)",
    duration: "34–38 mins + walk",
    frequency: "Every 5–31 mins",
    fare: "£3 / £6 return",
  },
  {
    from: "Premier Inn Slough Trading Estate",
    to: "LEGOLAND",
    bus: "X74 -> 702 / 703",
    fromStop: "Buckingham Ave -> Landmark Place",
    toStop: "LEGOLAND Park & Ride (Stop LP)",
    duration: "~45–60 mins",
    frequency: "Regular",
    fare: "£3 / £6 return",
  },
  {
    from: "Windsor Castle",
    to: "LEGOLAND",
    bus: "702",
    fromStop: "Windsor Parish Church (Stop HB) · 2 min walk",
    toStop: "LEGOLAND Park & Ride (Stop LP)",
    duration: "~20 mins",
    frequency: "Frequent",
    fare: "£3 / £6 return",
  },
  {
    from: "Heathrow Terminal 5",
    to: "LEGOLAND",
    bus: "703",
    fromStop: "Heathrow Terminal 5",
    toStop: "LEGOLAND Park & Ride (Stop LP)",
    duration: "~65–70 mins",
    frequency: "~Every 30 mins",
    fare: "£3 / £6 return",
  },
];
const LEGOLAND_LOCAL_TAXI_POINTS = [
  {
    id: "slough-station",
    label: "Slough Station",
    pickup: "SL1 1XW",
    duration: "10–20 mins",
    note: "Best fast last-mile fallback from rail.",
  },
  {
    id: "premier-inn-central-south",
    label: "Premier Inn Slough Central South",
    pickup: "SL1 2EL",
    duration: "15–20 mins",
    note: "Near Landmark Place if you prefer bus instead.",
  },
  {
    id: "travelodge-slough",
    label: "Travelodge Slough",
    pickup: "SL1 1PG",
    duration: "15–20 mins",
    note: "Short walk to bus stop or quick taxi.",
  },
  {
    id: "copthorne-slough",
    label: "Copthorne Hotel Slough-Windsor",
    pickup: "SL1 2YE",
    duration: "15–20 mins",
    note: "Taxi is easier than the long walk to Albert Street.",
  },
  {
    id: "premier-inn-trading-estate",
    label: "Premier Inn Slough Trading Estate",
    pickup: "SL1 4QZ",
    duration: "15–25 mins",
    note: "Useful if you are staying west of central Slough.",
  },
  {
    id: "windsor-castle",
    label: "Windsor Castle",
    pickup: "SL4 1NJ",
    duration: "10–15 mins",
    note: "Good if you are staying in Windsor town centre.",
  },
  {
    id: "heathrow-t5",
    label: "Heathrow Terminal 5",
    pickup: "TW6 2GB",
    duration: "20–30 mins",
    note: "Usually much faster than the 703 bus.",
  },
];
const LEGOLAND_ONSITE_HOTELS = [
  {
    id: "resort",
    name: "LEGOLAND Resort Hotel",
    badge: "Best for first-time visitors",
    walk: "2 min walk",
    vibe: "Most immersive full LEGO stay",
    roomStyle: "Pirate, Kingdom, Adventure, LEGO Friends, NINJAGO",
    bathroom: "Ensuite",
    breakfast: "Included at Bricks",
    parking: "Free",
    entertainment: "Indoor play, splash pool, evening entertainment",
    extras: "In-room treasure hunt with gift",
    package: 614,
  },
  {
    id: "castle",
    name: "LEGOLAND Castle Hotel",
    badge: "Best for a calmer premium feel",
    walk: "2 min walk",
    vibe: "Quieter premium version of the main stay",
    roomStyle: "Knight and Wizard themed rooms",
    bathroom: "Ensuite",
    breakfast: "Included at Tournament Tavern",
    parking: "Free",
    entertainment: "Core hotel perks, pool access, evening entertainment",
    extras: "Similar perks to the main hotel with a calmer feel",
    package: 614,
  },
  {
    id: "woodland",
    name: "Woodland Village",
    badge: "Best for value and a quieter stay",
    walk: "10–15 min walk or shuttle",
    vibe: "Holiday park / Center Parcs feel",
    roomStyle: "Lodges and barrels, less hotel-like",
    bathroom: "Some options have shared facilities",
    breakfast: "Included with package",
    parking: "Free",
    entertainment: "Outdoor play, nature trail, evening entertainment",
    extras: "Best value, but less immersive and further from the gate",
    package: 434,
  },
];
const LEGOLAND_HOTEL_COMPARISON_ROWS = [
  {
    label: "Walk to park",
    resort: "2 mins",
    castle: "2 mins",
    woodland: "10–15 mins",
  },
  {
    label: "Overall feel",
    resort: "Full LEGO immersion",
    castle: "Premium and calmer",
    woodland: "Relaxed and quieter",
  },
  {
    label: "Bathroom setup",
    resort: "Ensuite",
    castle: "Ensuite",
    woodland: "Some shared in barrels",
  },
  {
    label: "Breakfast",
    resort: "Included",
    castle: "Included",
    woodland: "Included",
  },
  {
    label: "Free parking",
    resort: "Yes",
    castle: "Yes",
    woodland: "Yes",
  },
  {
    label: "Early ride access",
    resort: "Usually included if tickets included",
    castle: "Usually included if tickets included",
    woodland: "Usually included if tickets included",
  },
  {
    label: "Entertainment",
    resort: "Indoor play + splash pool",
    castle: "Shared hotel facilities",
    woodland: "Outdoor play + nature trail",
  },
  {
    label: "Typical package price",
    resort: "£614",
    castle: "£614",
    woodland: "£434",
  },
];

const defaultDateTime = () => {
  const now = new Date();
  const target = new Date(now);
  target.setDate(now.getDate() + 30);
  target.setHours(10, 0, 0, 0);
  const pad = (n) => String(n).padStart(2, "0");
  return `${target.getFullYear()}-${pad(target.getMonth() + 1)}-${pad(target.getDate())}T${pad(target.getHours())}:${pad(target.getMinutes())}`;
};
const FALLBACK_LEGO_DATETIME = defaultDateTime();
const FALLBACK_LEGO_DATE = FALLBACK_LEGO_DATETIME.split("T")[0];

const clampNumber = (val, min, max, fallback) => {
  const n = Number(val);
  if (Number.isNaN(n)) return fallback;
  return Math.min(max, Math.max(min, n));
};

const money = (v, opts = {}) => {
  if (v == null || Number.isNaN(Number(v))) return "—";
  const decimals = opts.decimals ?? 0;
  const prefix = opts.prefix ?? "£";
  return `${prefix}${Number(v).toFixed(decimals)}`;
};

const rangeText = (min, max) => {
  if (min == null && max == null) return "—";
  if (min === max) return money(min);
  return `${money(min)} – ${money(max)}`;
};

const formatTime = (t) => {
  if (!t) return "—";
  if (typeof t === "string" && t.length >= 5) return t.slice(0, 5);
  return t;
};

const formatDisplayDate = (d) => {
  if (!d) return null;
  const parsed = new Date(d);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toLocaleDateString("en-GB", { day: "numeric", month: "short", year: "numeric" });
};

const stationLabelFromCrs = (crs, fallback = "Station") => {
  const code = String(crs || "").toUpperCase();
  if (code === "WAT") return "Waterloo";
  if (code === "VIC") return "Victoria";
  if (code === "PAD") return "Paddington";
  if (code === "CHX") return "Charing Cross";
  if (code === "CLJ") return "Clapham Jn";
  if (code === "HAY") return "Hayes & Harlington";
  if (code === "SLO") return "Slough";
  if (code === "WNR") return "Windsor";
  return crs || fallback;
};

const parseClockDate = (dateStr, timeStr) => {
  if (!dateStr || !timeStr) return null;
  const raw = `${dateStr}T${String(timeStr).slice(0, 5)}:00`;
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
};

const luggageToBagCount = (luggage) => {
  if (luggage === "none") return 0;
  if (luggage === "heavy") return 2;
  return 1;
};

const addMinutes = (date, mins) => {
  if (!date || !Number.isFinite(Number(mins))) return null;
  return new Date(date.getTime() + Number(mins) * 60000);
};

const subtractMinutes = (date, mins) => {
  if (!date || !Number.isFinite(Number(mins))) return null;
  return new Date(date.getTime() - Number(mins) * 60000);
};

const formatTimelineClock = (date) => {
  if (!(date instanceof Date) || Number.isNaN(date.getTime())) return "—";
  return date.toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit", hour12: false });
};

const asDateOnly = (value) => {
  if (!value) return "";
  const raw = String(value);
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) return "";
  return parsed.toISOString().slice(0, 10);
};

const taxiSpeedMphForDistance = (dist) => {
  if (dist <= 15) return 30;
  if (dist <= 60) return 42;
  return 52;
};

const addMonths = (dateStr, monthsToAdd) => {
  const base = new Date(`${dateStr}T00:00:00`);
  if (Number.isNaN(base.getTime())) return null;
  const shifted = new Date(base);
  shifted.setMonth(shifted.getMonth() + monthsToAdd);
  const pad = (n) => String(n).padStart(2, "0");
  return `${shifted.getFullYear()}-${pad(shifted.getMonth() + 1)}`;
};


const isFullPostcode = (pc) => {
  if (!pc) return false;
  return /^[A-Z]{1,2}\d[A-Z\d]?\s*\d[A-Z]{2}$/i.test(pc.trim());
};

const normalizePostcode = (pc) => String(pc || "").toUpperCase().replace(/\s+/g, " ").trim();
const changeCountText = (count) => `${count} ${count === 1 ? "change" : "changes"}`;
const toApiDateTime = (value) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  return parsed.toISOString();
};

const toSafeRailLiveTime = (value, minMinutesAhead = 90) => {
  if (!value) return null;
  const parsed = new Date(value);
  if (Number.isNaN(parsed.getTime())) return null;
  const minFuture = new Date(Date.now() + minMinutesAhead * 60 * 1000);
  return parsed.getTime() < minFuture.getTime() ? null : parsed.toISOString();
};

const toSafeTaxiPreviewTime = (value, minHoursAhead = 5) => {
  const selected = value ? new Date(value) : null;
  const minFuture = new Date(Date.now() + minHoursAhead * 60 * 60 * 1000);
  if (!(selected instanceof Date) || Number.isNaN(selected.getTime())) return minFuture.toISOString();
  return (selected.getTime() < minFuture.getTime() ? minFuture : selected).toISOString();
};

const toSafeBookableTaxiTime = (value, minHoursAhead = 25) => {
  const selected = value ? new Date(value) : null;
  const minFuture = new Date(Date.now() + minHoursAhead * 60 * 60 * 1000);
  if (!(selected instanceof Date) || Number.isNaN(selected.getTime())) return minFuture.toISOString();
  return (selected.getTime() < minFuture.getTime() ? minFuture : selected).toISOString();
};

const railRouteLine = ({ originName, originCrs, interchangesCrs, gatewayName, gatewayCrs }) => {
  const parts = [
    originName || stationLabelFromCrs(originCrs, "Origin"),
    ...(Array.isArray(interchangesCrs) ? interchangesCrs.map((crs) => stationLabelFromCrs(crs, crs)) : []),
    gatewayName || stationLabelFromCrs(gatewayCrs, "Gateway"),
  ].filter(Boolean);
  return parts.join(" → ");
};

const buildWeatherTargetLocal = (dateStr, openTime) => {
  if (!dateStr || !openTime) return null;
  return `${dateStr}T${String(openTime).slice(0, 5)}`;
};

export default function LegolandBudgetPlanner() {
  const navigate = useNavigate();
  const initialTrip = {
    pickup: "SW1A 1AA",
    returnSame: false,
    datetime: FALLBACK_LEGO_DATETIME,
    adults: 2,
    children: 1,
    under90: 0,
    elderly: false,
    stroller: true,
    luggage: "light",
    distanceMiles: 25,
    transport: "compare",
    carType: "petrol",
    mpg: 38,
    fuelPrice: 1.55,
    parkingType: "standard",
    ulez: true,
    includeWearTear: false,
    foodChoice: "mixed",
    snackStop: true,
    weatherSensitivity: "medium",
    walkingTolerance: "medium",
    tiredKids: true
  };
  const [formTrip, setFormTrip] = useState(initialTrip);
  const [appliedTrip, setAppliedTrip] = useState(initialTrip);
  const trip = appliedTrip;
  // Default date/time prefilled for SEO/UX; next-open lookup can be added later if needed.

  useEffect(() => {
    if (formTrip.carType === "ev" && formTrip.parkingType !== "priority") {
      setFormTrip((prev) => ({ ...prev, parkingType: "priority" }));
    }
  }, [formTrip.carType, formTrip.parkingType]);

  const payingAdults = clampNumber(trip.adults, 0, 6, 0);
  const payingChildren = clampNumber(trip.children, 0, 6, 0);
  const littleOnes = clampNumber(trip.under90, 0, 6, 0);
  const partySize = payingAdults + payingChildren + littleOnes + (trip.elderly ? 1 : 0);
  const distance = clampNumber(trip.distanceMiles, 1, 180, 25);
  const tripLegs = trip.returnSame ? 2 : 1;
  const selectedDate = trip.datetime ? trip.datetime.split("T")[0] : "";
  const formattedSelectedDate = formatDisplayDate(selectedDate);
  const partySummaryParts = [
    `${payingAdults} adult${payingAdults === 1 ? "" : "s"}`,
    payingChildren > 0 ? `${payingChildren} child${payingChildren === 1 ? "" : "ren"}` : null,
    littleOnes > 0 ? `${littleOnes} under-90cm` : null
  ].filter(Boolean);
  const partySummary = partySummaryParts.join(" • ");
  const totalCostLabel = `Total day cost${partySummary ? ` (${partySummary})` : ""}`;
  const hiddenOptionsSummary = [
    partySummary,
    formTrip.transport === "compare" ? "Compare all modes" : formTrip.transport === "public" ? "Public transport" : formTrip.transport === "drive" ? "Drive" : "Taxi",
    formTrip.foodChoice === "packed"
      ? "Packed food"
      : formTrip.foodChoice === "eat"
      ? "Eat at park"
      : formTrip.foodChoice === "buffet"
      ? "Buffet (Merlin)"
      : "Mixed food"
  ].filter(Boolean).join(" • ");
  const isWinter = useMemo(() => {
    const month = trip.datetime ? new Date(trip.datetime).getMonth() : new Date().getMonth();
    return [10, 11, 0, 1].includes(month); // Nov–Feb as winter pricing
  }, [trip.datetime]);
  const parkingPrices = useMemo(
    () => ({
      standard: isWinter ? 6 : 12,
      priority: isWinter ? 9 : 18,
      disabled: isWinter ? 6 : 12
    }),
    [isWinter]
  );

  const [monthData, setMonthData] = useState({ month: null, days: [] });
  const [monthLoading, setMonthLoading] = useState(false);
  const [monthError, setMonthError] = useState(null);
  const [monthStatus, setMonthStatus] = useState(null);
  const [initialDateResolved, setInitialDateResolved] = useState(false);
  const [dayData, setDayData] = useState(null);
  const [dayLoading, setDayLoading] = useState(false);
  const [dayError, setDayError] = useState(null);
  const [dayStatus, setDayStatus] = useState(null);
  const [distanceStatus, setDistanceStatus] = useState(null);
  const [distanceError, setDistanceError] = useState(null);
  const [postcodeError, setPostcodeError] = useState(null);
  const [postcodeSuggestions, setPostcodeSuggestions] = useState([]);
  const [postcodeLookupLoading, setPostcodeLookupLoading] = useState(false);
  const [validatedPickup, setValidatedPickup] = useState(null);
  const [showPostcodeSuggestions, setShowPostcodeSuggestions] = useState(false);
  const [showPickupNudge, setShowPickupNudge] = useState(true);
  const [refreshFlash, setRefreshFlash] = useState(false);
  const [lastChangedField, setLastChangedField] = useState(null);
  const [liveTaxiQuote, setLiveTaxiQuote] = useState(null);
  const [liveRailPlan, setLiveRailPlan] = useState(null);
  const [parkWeatherSummary, setParkWeatherSummary] = useState(null);
  const [localTransportOpen, setLocalTransportOpen] = useState(false);
  const [localTaxiQuotes, setLocalTaxiQuotes] = useState({});
  const [localTaxiLoading, setLocalTaxiLoading] = useState(false);
  const [localTaxiBookingId, setLocalTaxiBookingId] = useState(null);
  const [transportRefreshNonce, setTransportRefreshNonce] = useState(0);
  const refreshTimer = useRef(null);
  const initialLiveBootstrapRan = useRef(false);
  const userHasInteracted = useRef(false);
  const localTransportRef = useRef(null);
  const isMobile = typeof window !== "undefined" ? window.innerWidth <= 640 : false;
  const heroInputStyle = {
    minHeight: isMobile ? 44 : 46,
    padding: "8px 12px",
    fontSize: 16,
    background: "linear-gradient(180deg, #ffffff 0%, #fffaf0 100%)",
    border: "1px solid #d7e6f5",
    boxShadow: "inset 0 1px 0 rgba(255,255,255,0.75), 0 4px 12px rgba(15, 98, 182, 0.05)"
  };

  useEffect(() => {
    const handle = setTimeout(() => setShowPickupNudge(false), 4800);
    return () => clearTimeout(handle);
  }, []);

  const fetchInitialLiveTransport = async ({ pickup, departAt, adults, children, passengers, bags }) => {
    try {
      const [taxiRes, railRes] = await Promise.allSettled([
        api.post("/api/quote-preview", {
          pickup,
          dropoff: DEST_POSTCODE,
          passengers: Math.max(1, passengers || 1),
          bags: Math.max(0, bags || 0),
          pickup_time: toSafeTaxiPreviewTime(departAt),
        }),
        api.post("/api/legoland-rail/plan", {
          pickup_postcode: pickup,
          depart_at: departAt,
          adults,
          children,
          bags: Math.max(0, bags || 0),
          return_same_day: Boolean(initialTrip.returnSame),
        }),
      ]);

      if (taxiRes.status === "fulfilled") {
        const vehicles = Array.isArray(taxiRes.value.data?.vehicles) ? taxiRes.value.data.vehicles : [];
        const canLiveBook = Boolean(taxiRes.value.data?.eligibility?.can_live_book) && !Boolean(taxiRes.value.data?.eligibility?.enquiry_only);
        const priced = vehicles
          .filter((v) => v?.price != null)
          .map((v) => ({ ...v, price: Number(v.price) }))
          .sort((a, b) => a.price - b.price);
        const cheapestLive = priced.find((v) => String(v.book_mode || "").toLowerCase() === "live") || null;
        const cheapest = canLiveBook ? cheapestLive : null;
        setLiveTaxiQuote(
          cheapest
            ? {
                vehicle: cheapest.code || null,
                oneWayFare: Number(cheapest.price),
                distanceKm: taxiRes.value.data?.distance_km != null ? Number(taxiRes.value.data.distance_km) : null,
                source: "quote-preview-v2",
                liveBookable: true,
              }
            : null
        );
      }

      if (railRes.status === "fulfilled") {
        setLiveRailPlan(railRes.value.data?.success ? railRes.value.data : null);
      }
    } catch (err) {}
  };

  useEffect(() => {
    if (!formTrip.datetime) {
      setFormTrip(prev => ({ ...prev, datetime: FALLBACK_LEGO_DATETIME }));
    }
  }, [formTrip.datetime]);

  useEffect(() => {
    let cancelled = false;
    const today = new Date();
    const pad = (n) => String(n).padStart(2, "0");
    const todayStr = `${today.getFullYear()}-${pad(today.getMonth() + 1)}-${pad(today.getDate())}`;

    const findNextOpenDate = async () => {
      try {
        for (let offset = 0; offset < 3; offset += 1) {
          const month = addMonths(todayStr, offset);
          if (!month) continue;
          const res = await api.get("/api/legoland/month", { params: { month } });
          const days = Array.isArray(res.data?.days) ? res.data.days : [];
          const nextOpen = days.find((day) => day?.is_open && asDateOnly(day?.visit_date) > todayStr);
          if (nextOpen?.visit_date) {
            if (!cancelled) {
              const nextOpenDate = asDateOnly(nextOpen.visit_date);
              console.info("[LEGOLAND bootstrap]", {
                today: todayStr,
                resolvedNextOpenDate: nextOpenDate,
                rawVisitDate: nextOpen.visit_date,
              });
              const nextDateTime = nextOpenDate ? `${nextOpenDate}T10:00` : FALLBACK_LEGO_DATETIME;
              setFormTrip((prev) => {
                const prevDate = prev.datetime ? prev.datetime.split("T")[0] : "";
                if (prevDate && prevDate !== FALLBACK_LEGO_DATE) return prev;
                return { ...prev, datetime: nextDateTime };
              });
              setAppliedTrip((prev) => {
                const prevDate = prev.datetime ? prev.datetime.split("T")[0] : "";
                if (prevDate && prevDate !== FALLBACK_LEGO_DATE) return prev;
                return { ...prev, datetime: nextDateTime };
              });
              setInitialDateResolved(true);
              const canonicalPickup = normalizePostcode(formTrip.pickup) || formTrip.pickup;
              initialLiveBootstrapRan.current = true;
              setTimeout(() => {
                setAppliedTrip((prev) => ({
                  ...prev,
                  datetime: nextDateTime,
                  pickup: canonicalPickup
                }));
              }, 0);
              fetchInitialLiveTransport({
                pickup: canonicalPickup,
                departAt: toApiDateTime(nextDateTime),
                adults: clampNumber(initialTrip.adults, 0, 6, 0),
                children: clampNumber(initialTrip.children, 0, 6, 0),
                passengers: clampNumber(initialTrip.adults, 0, 6, 0) + clampNumber(initialTrip.children, 0, 6, 0),
                bags: luggageToBagCount(initialTrip.luggage),
              });
            }
            return;
          }
        }
      } catch (err) {
        console.warn("LEGOLAND next-open lookup failed", err?.response?.status || err?.message || err);
      }

      if (!cancelled) {
        setInitialDateResolved(true);
      }
    };

    findNextOpenDate();

    return () => {
      cancelled = true;
    };
  }, []);

  const triggerUpdateFlash = (field) => {
    userHasInteracted.current = true;
    if (field === "pickup") setShowPickupNudge(false);
    setLastChangedField(field);
    setRefreshFlash(true);
    if (refreshTimer.current) clearTimeout(refreshTimer.current);
    refreshTimer.current = setTimeout(() => setRefreshFlash(false), 900);
  };

  const estimateMilesFromPostcode = (pc) => {
    if (!pc) return null;
    const outward = String(pc).trim().toUpperCase().split(" ")[0];
    const prefix = outward.replace(/[^A-Z0-9]/g, "");
    const bands = {
      SL: 6,
      TW: 10,
      UB: 13,
      KT: 18,
      GU: 22,
      RG: 18,
      HP: 18,
      WD: 22,
      HA: 20,
      W: 19,
      SW: 23,
      SE: 30,
      EC: 25,
      WC: 25,
      E: 33,
      N: 30,
      NW: 23,
      OX: 40,
      LU: 32
    };
    const match = Object.keys(bands).find((k) => prefix.startsWith(k));
    return match ? bands[match] : null;
  };

  useEffect(() => {
    const est = estimateMilesFromPostcode(formTrip.pickup);
    if (est) {
      setFormTrip((prev) => ({ ...prev, distanceMiles: est }));
      if (!userHasInteracted.current) {
        setAppliedTrip((prev) => ({ ...prev, distanceMiles: est }));
      }
    }
  }, [formTrip.pickup]);

  useEffect(() => {
    const query = normalizePostcode(formTrip.pickup).replace(/\s/g, "");
    if (query.length < 3) {
      setPostcodeSuggestions([]);
      setShowPostcodeSuggestions(false);
      return;
    }

    const controller = new AbortController();
    const handle = setTimeout(async () => {
      try {
        const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(query)}/autocomplete?limit=6`, {
          signal: controller.signal
        });
        const data = await res.json();
        if (!controller.signal.aborted) {
          setPostcodeSuggestions(Array.isArray(data?.result) ? data.result : []);
          setShowPostcodeSuggestions(Array.isArray(data?.result) && data.result.length > 0);
        }
      } catch (err) {
        if (!controller.signal.aborted) {
          setPostcodeSuggestions([]);
          setShowPostcodeSuggestions(false);
        }
      }
    }, 180);

    return () => {
      controller.abort();
      clearTimeout(handle);
    };
  }, [formTrip.pickup]);

  useEffect(() => {
    const candidate = normalizePostcode(formTrip.pickup);
    if (!candidate) {
      setValidatedPickup(null);
      setPostcodeError(null);
      setDistanceError(null);
      setDistanceStatus(null);
      return;
    }
    if (!isFullPostcode(candidate)) {
      setValidatedPickup(null);
      setPostcodeError(null);
      setDistanceError(null);
      setDistanceStatus(null);
      return;
    }

    const controller = new AbortController();
    const handle = setTimeout(async () => {
      setPostcodeLookupLoading(true);
      try {
        const res = await fetch(`https://api.postcodes.io/postcodes/${encodeURIComponent(candidate.replace(/\s/g, ""))}`, {
          signal: controller.signal
        });
        const data = await res.json();
        if (controller.signal.aborted) return;

        if (res.ok && data?.result?.postcode) {
          const canonical = data.result.postcode;
          setValidatedPickup(canonical);
          setPostcodeError(null);
          if (canonical !== formTrip.pickup) {
            setFormTrip((prev) => (prev.pickup === formTrip.pickup ? { ...prev, pickup: canonical } : prev));
            if (!userHasInteracted.current) {
              setAppliedTrip((prev) => (prev.pickup === formTrip.pickup ? { ...prev, pickup: canonical } : prev));
            }
          }
          return;
        }

        setValidatedPickup(null);
        setPostcodeError("Enter a valid UK postcode.");
      } catch (err) {
        if (!controller.signal.aborted) {
          setValidatedPickup(null);
          setPostcodeError("Postcode check unavailable right now.");
        }
      } finally {
        if (!controller.signal.aborted) {
          setPostcodeLookupLoading(false);
        }
      }
    }, 250);

    return () => {
      controller.abort();
      clearTimeout(handle);
      setPostcodeLookupLoading(false);
    };
  }, [formTrip.pickup]);

  // Fetch precise distance using backend (OSRM)
  useEffect(() => {
    if (!validatedPickup) return;
    if (!formTrip.pickup) {
      setDistanceError(null);
      setDistanceStatus(null);
      return;
    }
    const handle = setTimeout(async () => {
      try {
        const res = await api.get("/api/legoland/distance", { params: { from: validatedPickup } });
        if (res.data?.miles) {
          setFormTrip((prev) => ({ ...prev, distanceMiles: res.data.miles }));
          if (!userHasInteracted.current) {
            setAppliedTrip((prev) => ({ ...prev, distanceMiles: res.data.miles }));
          }
          setDistanceStatus(res.data.source || "osrm");
          setDistanceError(null);
        }
      } catch (err) {
        const msg = err?.response?.data?.error || "Distance lookup unavailable — using estimate.";
        setDistanceError(msg);
      }
    }, 200);
    return () => clearTimeout(handle);
  }, [formTrip.pickup, validatedPickup]);

  const hasPendingChanges = useMemo(
    () => JSON.stringify(formTrip) !== JSON.stringify(appliedTrip),
    [formTrip, appliedTrip]
  );

  const applyPlannerInputs = () => {
    userHasInteracted.current = true;
    setAppliedTrip({
      ...formTrip,
      pickup: validatedPickup || normalizePostcode(formTrip.pickup) || formTrip.pickup
    });
    setTransportRefreshNonce((n) => n + 1);
  };

  const handlePostcodeSuggestionPick = (postcode) => {
    triggerUpdateFlash("pickup");
    setFormTrip((prev) => ({ ...prev, pickup: postcode }));
    setShowPostcodeSuggestions(false);
  };

  const visiblePostcodeSuggestions = useMemo(() => {
    const current = normalizePostcode(formTrip.pickup);
    return postcodeSuggestions.filter((postcode) => normalizePostcode(postcode) !== current);
  }, [formTrip.pickup, postcodeSuggestions]);

  // Fetch month calendar + prices
  useEffect(() => {
    if (!selectedDate) return;
    const month = selectedDate.slice(0, 7);
    if (monthData.month === month || monthLoading) return;
    const fetchMonth = async () => {
      setMonthLoading(true);
      setMonthError(null);
      setMonthStatus(null);
      try {
        const res = await api.get("/api/legoland/month", { params: { month } });
        setMonthStatus(res.status);
        setMonthData(res.data || { month, days: [] });
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.error;
        console.warn("LEGOLAND month fetch failed", { status, detail, month });
        if (status === 404) {
          setMonthError("No LEGOLAND calendar data returned for this month.");
        } else {
          setMonthError("Could not load LEGOLAND calendar for this month.");
        }
        setMonthData({ month, days: [] });
        setMonthStatus(status || "error");
      } finally {
        setMonthLoading(false);
      }
    };
    fetchMonth();
  }, [monthData.month, monthLoading, selectedDate]);

  // Fetch day view (use month data as soft fallback)
  useEffect(() => {
    if (!selectedDate) return;
    setDayData(null);
    setDayError(null);
    setDayStatus(null);
    const fallback = monthData.days.find((d) => d.visit_date === selectedDate);
    if (fallback) setDayData(fallback);
    const fetchDay = async () => {
      setDayLoading(true);
      setDayError(null);
      setDayStatus(null);
      try {
        const res = await api.get("/api/legoland/day", { params: { date: selectedDate } });
        if (res.data) setDayData(res.data);
        setDayStatus(res.status);
      } catch (err) {
        const status = err?.response?.status;
        const detail = err?.response?.data?.error;
        console.warn("LEGOLAND day fetch failed", { status, detail, selectedDate });
        if (status === 404 && fallback) {
          setDayError(null);
          setDayData(fallback);
        } else if (status === 404) {
          setDayError("No calendar data returned for this date.");
          setDayData({ is_open: false, open_time: null, close_time: null, price_from_gbp: null });
        } else {
          setDayError("Could not load live park status for this date.");
          setDayData({ is_open: false, open_time: null, close_time: null, price_from_gbp: null });
        }
        setDayStatus(status || "error");
      } finally {
        setDayLoading(false);
      }
    };
    fetchDay();
  }, [monthData.days, selectedDate]);

  const trafficProfile = useMemo(() => {
    if (!trip.datetime) return { label: "Typical daytime", padding: 14 };
    const hour = new Date(trip.datetime).getHours();
    if (hour >= 7 && hour <= 9) return { label: "AM peak (expect queues)", padding: 24 };
    if (hour >= 16 && hour <= 19) return { label: "PM peak (slow)", padding: 26 };
    if (hour >= 11 && hour <= 14) return { label: "Late morning shoulder", padding: 14 };
    return { label: "Off-peak", padding: 10 };
  }, [trip.datetime]);
  const apiDepartAt = useMemo(() => toApiDateTime(trip.datetime), [trip.datetime]);
  const apiRailDepartAt = useMemo(() => toSafeRailLiveTime(trip.datetime), [trip.datetime]);
  const apiTaxiPreviewAt = useMemo(() => toSafeTaxiPreviewTime(trip.datetime), [trip.datetime]);
  const apiTaxiBookableAt = useMemo(() => toSafeBookableTaxiTime(trip.datetime), [trip.datetime]);
  const liveTransportReady = useMemo(() => {
    const appliedDate = trip.datetime ? trip.datetime.split("T")[0] : "";
    return initialDateResolved || (appliedDate && appliedDate !== FALLBACK_LEGO_DATE);
  }, [initialDateResolved, trip.datetime]);

  const isUpdating = refreshFlash || dayLoading || monthLoading || !initialDateResolved;

  const seasonalWeather = useMemo(() => {
    const month = trip.datetime ? new Date(trip.datetime).getMonth() : new Date().getMonth();
    if ([11, 0, 1].includes(month)) return { label: "Cold + rain risk", penalty: 10, note: "Pack layers and waterproofs." };
    if ([5, 6, 7].includes(month)) return { label: "Warm / busy", penalty: 6, note: "Hydration + hats recommended." };
    if ([3, 4, 8, 9].includes(month)) return { label: "Mild / changeable", penalty: 8, note: "Light rain possible; check on the day." };
    return { label: "Mixed", penalty: 8, note: "Check forecast morning of travel." };
  }, [trip.datetime]);

  const entryPricing = useMemo(() => {
    const livePrice = dayData?.price_from_gbp ? Number(dayData.price_from_gbp) : null;
    if (livePrice == null) {
      return {
        adultPrice: null,
        childPrice: null,
        adults: payingAdults + (trip.elderly ? 1 : 0),
        children: payingChildren,
        littleOnes,
        onlineTotal: null,
        walkupTotal: null,
        note: "No live ticket data for this date. Park assumed closed unless data appears."
      };
    }
    const adultPrice = livePrice; // use live snapshot when available
    const childPrice = livePrice;
    const walkupMultiplier = 1.35;
    const adults = payingAdults + (trip.elderly ? 1 : 0);
    const adultTotal = adults * adultPrice;
    const childTotal = payingChildren * childPrice;
    const underTotal = 0;
    const onlineTotal = adultTotal + childTotal + underTotal;
    return {
      adultPrice,
      childPrice,
      adults,
      children: payingChildren,
      littleOnes,
      onlineTotal,
      walkupTotal: Math.round(onlineTotal * walkupMultiplier),
      note: "Prices based on advance online tickets. Walk-up prices can be significantly higher."
    };
  }, [dayData, littleOnes, payingAdults, payingChildren, trip.elderly]);

  const foodEstimate = useMemo(() => {
    const payingHeads = partySize || 1;
    const ranges = {
      packed: { min: 0, max: 10, label: "Packed lunch", note: "Allow a small buffer for snacks or drinks." },
      mixed: { min: 15, max: 30, label: "Mixed", note: "Snack + 1 meal in-park per person." },
      eat: { min: 25, max: 40, label: "Eat at park", note: "Covers meals + drinks at LEGOLAND outlets." },
      buffet: {
        min: 0,
        max: 0,
        label: "Buffet (Merlin)",
        note: "Pizza & Pasta buffet with Merlin pricing: adults £16.75, children £10, usually with a refill soft drink.",
      },
    };
    const selected = ranges[trip.foodChoice] || ranges.mixed;
    if (trip.foodChoice === "buffet") {
      const adultHeads = payingAdults + (trip.elderly ? 1 : 0);
      const exact = Number((adultHeads * LEGOLAND_BUFFET_MERLIN.adult + payingChildren * LEGOLAND_BUFFET_MERLIN.child).toFixed(2));
      return {
        ...selected,
        min: exact,
        max: exact,
        exact: true,
        perPerson: `${money(LEGOLAND_BUFFET_MERLIN.adult, { decimals: 2 })} adult • ${money(LEGOLAND_BUFFET_MERLIN.child)} child`,
      };
    }
    const min = selected.min * payingHeads + (trip.snackStop ? 8 * payingHeads * 0.25 : 0);
    const max = selected.max * payingHeads + (trip.snackStop ? 12 * payingHeads * 0.25 : 0);
    return { ...selected, min: Math.round(min), max: Math.round(max), perPerson: `${money(selected.min)}–${money(selected.max)} pp` };
  }, [partySize, payingAdults, payingChildren, trip.elderly, trip.foodChoice, trip.snackStop]);

  useEffect(() => {
    if (!userHasInteracted.current && initialLiveBootstrapRan.current) return;
    if (!liveTransportReady || !trip.pickup || !apiTaxiPreviewAt) {
      setLiveTaxiQuote(null);
      return;
    }

    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const res = await api.post(
          "/api/quote-preview",
          {
            pickup: trip.pickup,
            dropoff: DEST_POSTCODE,
            passengers: Math.max(1, partySize || 1),
            bags: Math.max(0, partySize || 0),
            pickup_time: apiTaxiPreviewAt,
          }
        );
        if (cancelled) return;
        const vehicles = Array.isArray(res.data?.vehicles) ? res.data.vehicles : [];
        const canLiveBook = Boolean(res.data?.eligibility?.can_live_book) && !Boolean(res.data?.eligibility?.enquiry_only);
        const priced = vehicles
          .filter((v) => v?.price != null)
          .map((v) => ({ ...v, price: Number(v.price) }))
          .sort((a, b) => a.price - b.price);
        const cheapestLive = priced.find((v) => String(v.book_mode || "").toLowerCase() === "live") || null;
        const cheapest = canLiveBook ? cheapestLive : null;
        setLiveTaxiQuote(
          cheapest
            ? {
                vehicle: cheapest.code || null,
                oneWayFare: Number(cheapest.price),
                distanceKm: res.data?.distance_km != null ? Number(res.data.distance_km) : null,
                source: "quote-preview-v2",
                liveBookable: true,
              }
            : null
        );
      } catch (err) {
        if (!cancelled) {
          setLiveTaxiQuote(null);
        }
      }
    }, 180);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [apiTaxiPreviewAt, liveTransportReady, partySize, trip.pickup, transportRefreshNonce]);

  useEffect(() => {
    if (!userHasInteracted.current && initialLiveBootstrapRan.current) return;
    if (!liveTransportReady || !trip.pickup || !apiRailDepartAt) {
      setLiveRailPlan(null);
      return;
    }

    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const res = await api.post(
          "/api/legoland-rail/plan",
          {
            pickup_postcode: trip.pickup,
            depart_at: apiRailDepartAt,
            adults: payingAdults + (trip.elderly ? 1 : 0),
            children: payingChildren,
            bags: luggageToBagCount(trip.luggage),
            return_same_day: trip.returnSame,
          }
        );
        if (!cancelled) {
          setLiveRailPlan(res.data?.success ? res.data : null);
        }
      } catch (err) {
        if (!cancelled) {
          setLiveRailPlan(null);
        }
      }
    }, 220);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [apiRailDepartAt, liveTransportReady, payingAdults, payingChildren, trip.elderly, trip.pickup, transportRefreshNonce]);

  useEffect(() => {
    const targetLocal = dayData?.is_open ? buildWeatherTargetLocal(selectedDate, dayData?.open_time) : null;
    if (!targetLocal) {
      setParkWeatherSummary(null);
      return;
    }

    let cancelled = false;
    const handle = setTimeout(async () => {
      try {
        const res = await api.get("/api/open/weather/coords", {
          params: {
            lat: PARK_COORDS.lat,
            lon: PARK_COORDS.lon,
            targetLocal,
          },
        });
        if (!cancelled) {
          setParkWeatherSummary(res.data?.summary || null);
        }
      } catch (err) {
        if (!cancelled) setParkWeatherSummary(null);
      }
    }, 120);

    return () => {
      cancelled = true;
      clearTimeout(handle);
    };
  }, [dayData?.is_open, dayData?.open_time, selectedDate]);

  useEffect(() => {
    if (!localTransportOpen || !apiTaxiBookableAt) return;

    let cancelled = false;
    setLocalTaxiLoading(true);

    const run = async () => {
      const results = await Promise.allSettled(
        LEGOLAND_LOCAL_TAXI_POINTS.map(async (point) => {
          const res = await api.post("/api/quote-preview", {
            pickup: point.pickup,
            dropoff: DEST_POSTCODE,
            passengers: Math.min(Math.max(1, partySize || 1), 4),
            bags: Math.min(Math.max(0, Math.max((partySize || 1) - 1, 1)), 2),
            pickup_time: apiTaxiBookableAt,
          });

          const vehicles = Array.isArray(res.data?.vehicles) ? res.data.vehicles : [];
          const priced = vehicles
            .filter((v) => v?.price != null)
            .map((v) => ({ ...v, price: Number(v.price) }))
            .sort((a, b) => a.price - b.price);
          const saloon = priced.find((v) => String(v.code || "").toLowerCase() === "saloon");
          const chosen = saloon || priced[0] || null;

          return [
            point.id,
            chosen
              ? {
                  fare: Number(chosen.price),
                  vehicle: chosen.code || null,
                  source: "quote-preview-v2",
                }
              : null,
          ];
        })
      );

      if (cancelled) return;

      const next = {};
      results.forEach((result, idx) => {
        const point = LEGOLAND_LOCAL_TAXI_POINTS[idx];
        next[point.id] = result.status === "fulfilled" ? result.value[1] : null;
      });
      setLocalTaxiQuotes(next);
      setLocalTaxiLoading(false);
    };

    run().catch(() => {
      if (!cancelled) {
        setLocalTaxiQuotes({});
        setLocalTaxiLoading(false);
      }
    });

    return () => {
      cancelled = true;
    };
  }, [apiTaxiBookableAt, localTransportOpen, partySize]);


  const hassleScore = (mode, ctx) => {
    const base = { drive: 24, taxi: 16, public: 44 }[mode] ?? 30;
    let score = base;

    if (trip.luggage === "light") score += { drive: 2, taxi: 1, public: 4 }[mode] ?? 2;
    if (trip.luggage === "heavy") score += { drive: 6, taxi: 4, public: 12 }[mode] ?? 6;

    const kidLoad = payingChildren * (mode === "public" ? 3 : 2) + littleOnes * (mode === "public" ? 2 : 1);
    score += kidLoad;

    if (trip.elderly) score += { drive: 8, taxi: 6, public: 12 }[mode] ?? 8;
    if (trip.stroller) score += { drive: 6, taxi: 4, public: 10 }[mode] ?? 6;
    if (trip.tiredKids) score += { drive: 5, taxi: 3, public: 8 }[mode] ?? 4;

    if (trip.walkingTolerance === "low") score += { drive: 6, taxi: 2, public: 12 }[mode] ?? 6;
    if (trip.walkingTolerance === "medium") score += { drive: 2, taxi: 0, public: 4 }[mode] ?? 2;

    if (trip.weatherSensitivity === "high") score += seasonalWeather.penalty;
    if (trip.weatherSensitivity === "medium") score += Math.round(seasonalWeather.penalty * 0.5);

    score += Math.max(0, (ctx.walkMins - 8) / 2);
    score += ctx.changes ? ctx.changes * (mode === "public" ? 6 : 1) : 0;
    score = Math.min(98, Math.max(5, Math.round(score)));

    const label = score <= 30 ? "🟢 Low hassle" : score <= 60 ? "🟠 Medium hassle" : "🔴 High hassle";
    return { score, label };
  };

  const modelledTransportEstimates = useMemo(() => {
    const mpg = clampNumber(trip.mpg, 20, 80, trip.carType === "diesel" ? 50 : 38);
    const fuelPrice = trip.carType === "diesel" ? trip.fuelPrice || 1.62 : trip.carType === "petrol" ? trip.fuelPrice || 1.55 : trip.fuelPrice || 0.34;
    const daysAhead = (() => {
      if (!trip.datetime) return 0;
      const today = new Date();
      const target = new Date(trip.datetime);
      const diff = Math.max(0, target.getTime() - today.getTime());
      return Math.floor(diff / (1000 * 60 * 60 * 24));
    })();

    const railFareAdult = (dist) => {
      if (dist <= 10) return 4;
      if (dist <= 30) return 18;
      if (dist <= 60) return 40;
      if (dist <= 120) return 80;
      if (dist <= 200) return 120;
      return 170;
    };

    const applyAdvance = (base, ahead) => {
      let val = base;
      if (ahead >= 14) val = Math.max(base * 0.8, base - 10);
      else if (ahead >= 7) val = Math.max(base * 0.9, base - 5);
      return Math.round(val);
    };

    const driveSpeedMph = (dist) => {
      if (dist <= 15) return 32; // town + short A-roads
      if (dist <= 60) return 45; // mix A-road + motorway
      return 55; // mostly motorway
    };

    const enforcedParkingType = trip.carType === "ev" ? "priority" : trip.parkingType;
    const parkingCost = parkingPrices[enforcedParkingType] ?? parkingPrices.standard;

    const driveFuel = trip.carType === "ev"
      ? distance * tripLegs * 0.07 * fuelPrice
      : (distance * tripLegs / mpg) * fuelPrice * 4.546;
    const wear = trip.includeWearTear ? distance * tripLegs * 0.15 : 0;
    const ulez = trip.ulez ? 12.5 : 0;
    const driveTotal = driveFuel + parkingCost + wear + ulez;

    const driveWalk = 9;
    const driveDuration = Math.round((distance / driveSpeedMph(distance)) * 60 + trafficProfile.padding + driveWalk);

    const taxiBase = 18;
    const taxiPerMile = 2.05;
    const cheapCabGrab = (taxiBase + taxiPerMile * distance) * tripLegs;
    const uberMid = ((taxiBase - 2) + distance * 2.25) * tripLegs;
    const blackMid = ((taxiBase + 4) + distance * 2.75) * tripLegs;
    const range = (mid, band = 0.18) => ({ min: Math.round(mid * (1 - band)), max: Math.round(mid * (1 + band)) });
    const taxiWalk = 3;
    const taxiDuration = Math.round((distance / taxiSpeedMphForDistance(distance)) * 60 + trafficProfile.padding + taxiWalk);

    const modelAdult = applyAdvance(railFareAdult(distance), daysAhead);
    const railChild = Math.round(modelAdult * 0.6);
    const inLondonBand = distance <= 40;
    const tflAdult = inLondonBand ? 4.4 : 0;
    const tflChild = inLondonBand ? 2.2 : 0;
    const adults = payingAdults + (trip.elderly ? 1 : 0);
    const railFare = Math.round((adults * modelAdult + payingChildren * railChild) * (trip.returnSame ? 1 : 1));
    const tflFare = Math.round((adults * tflAdult + payingChildren * tflChild) * (trip.returnSame ? 2 : 1));
    const shuttleBus = Math.round((adults + payingChildren) * 3 * (trip.returnSame ? 2 : 1)); // park shuttle both ways
    const railWalk = (distance > 30 ? 18 : 14) + 6; // station to shuttle + park gates
    const railChanges = distance > 30 ? 2 : 1;
    const railDuration = Math.round(distance / 24 * 60 + 18 + railChanges * 8 + railWalk);

    return {
      drive: {
        total: Math.round(driveTotal),
        fuel: Math.round(driveFuel),
        parking: parkingCost,
        parkingType: enforcedParkingType,
        parkingBands: parkingPrices,
        isWinter,
        ulez,
        wear,
        walkMins: driveWalk,
        duration: driveDuration,
        hassle: hassleScore("drive", { walkMins: driveWalk, changes: 0 })
      },
      taxi: {
        cheapCabGrab: Math.round(cheapCabGrab),
        uber: range(uberMid, 0.14),
        black: range(blackMid, 0.20),
        totalMin: Math.round(Math.min(cheapCabGrab, range(uberMid).min, range(blackMid).min)),
        totalMax: Math.round(Math.max(cheapCabGrab, range(uberMid).max, range(blackMid).max)),
        walkMins: taxiWalk,
        duration: taxiDuration,
        hassle: hassleScore("taxi", { walkMins: taxiWalk, changes: 0 })
      },
      public: {
        railFare,
        tflFare,
        shuttleBus,
        total: railFare + tflFare + shuttleBus,
        walkMins: railWalk,
        duration: railDuration,
        changes: railChanges,
        hassle: hassleScore("public", { walkMins: railWalk, changes: railChanges })
      }
    };
  }, [
    distance,
    payingAdults,
    payingChildren,
    trafficProfile,
    trip.carType,
    trip.elderly,
    trip.includeWearTear,
    trip.mpg,
    trip.parkingType,
    parkingPrices,
    tripLegs,
    isWinter,
    trip.returnSame,
    trip.ulez,
    trip.fuelPrice,
    trip.weatherSensitivity,
    trip.walkingTolerance,
    trip.luggage,
    trip.stroller,
    trip.tiredKids,
    littleOnes
  ]);

  const transportEstimates = useMemo(() => {
    const next = {
      drive: { ...modelledTransportEstimates.drive },
      taxi: { ...modelledTransportEstimates.taxi },
      public: { ...modelledTransportEstimates.public },
    };

    if (liveTaxiQuote?.liveBookable && liveTaxiQuote?.oneWayFare != null) {
      const liveTaxiFare = Number((liveTaxiQuote.oneWayFare * tripLegs).toFixed(2));
      const liveTaxiDistanceMiles = liveTaxiQuote.distanceKm != null ? Number(liveTaxiQuote.distanceKm) * 0.621371 : null;
      const liveTaxiDuration = liveTaxiDistanceMiles != null
        ? Math.round((liveTaxiDistanceMiles / taxiSpeedMphForDistance(liveTaxiDistanceMiles)) * 60 + trafficProfile.padding + next.taxi.walkMins)
        : next.taxi.duration;
      next.taxi = {
        ...next.taxi,
        live: true,
        source: liveTaxiQuote.source,
        vehicle: liveTaxiQuote.vehicle,
        cheapCabGrab: liveTaxiFare,
        duration: liveTaxiDuration,
        totalMin: Math.round(Math.min(liveTaxiFare, next.taxi.uber.min, next.taxi.black.min)),
        totalMax: Math.round(Math.max(liveTaxiFare, next.taxi.uber.max, next.taxi.black.max)),
      };
    }

    const liveRailData = liveRailPlan?.data;
    if (liveRailData?.total_public_transport_cost_gbp != null) {
      const walkMins =
        Number(liveRailData.access_walk_mins || 0) +
        Number(liveRailData.last_mile_walk_mins || 0);
      const changes = Number(liveRailData.interchanges || 0);
      next.public = {
        ...next.public,
        live: liveRailPlan?.mode !== "modelled_fallback",
        sourceMode: liveRailPlan?.mode || null,
        gatewayCrs: liveRailData.gateway_crs || null,
        gatewayName: liveRailData.gateway_name || null,
        originCrs: liveRailData.origin_selection?.preferred_crs || null,
        originName: liveRailData.origin_selection?.preferred_station?.common_name || null,
        interchangesCrs: Array.isArray(liveRailData.best_journey?.interchanges_crs) ? liveRailData.best_journey.interchanges_crs : [],
        railFare: Number(liveRailData.rail_fare_gbp || 0),
        accessTaxiGbp: Number(liveRailData.access_taxi_gbp || 0),
        lastMileTaxiGbp: Number(liveRailData.last_mile_taxi_gbp || 0),
        total: Number(liveRailData.total_public_transport_cost_gbp || 0),
        walkMins,
        duration: Number(liveRailData.total_public_transport_duration_mins || next.public.duration),
        changes,
        accessMode: liveRailData.access_mode || null,
        accessTaxiDurationMins: Number(liveRailData.access_taxi_duration_mins || 0),
        lastMileTaxiDurationMins: Number(liveRailData.last_mile_taxi_duration_mins || 0),
        bestJourneyDepart: liveRailData.best_journey?.depart || null,
        bestJourneyArrive: liveRailData.best_journey?.arrive || null,
        sourceNotes: liveRailData.source_notes || null,
        hassle: hassleScore("public", { walkMins, changes }),
      };
    }

    return next;
  }, [hassleScore, liveRailPlan, liveTaxiQuote, modelledTransportEstimates, trafficProfile, tripLegs]);

  const totalDayCosts = useMemo(() => {
    const entry = entryPricing.onlineTotal;
    if (entry == null) return null;
    const foodMid = Math.round((foodEstimate.min + foodEstimate.max) / 2);

    const driveTotal = entry + transportEstimates.drive.total + foodMid;
    const taxiBase = entry + transportEstimates.taxi.cheapCabGrab + foodMid;
    const publicTotal = entry + transportEstimates.public.total + foodMid;
    const taxiMinTransport = transportEstimates.taxi.live ? transportEstimates.taxi.cheapCabGrab : transportEstimates.taxi.totalMin;
    const taxiMaxTransport = transportEstimates.taxi.live ? transportEstimates.taxi.cheapCabGrab : transportEstimates.taxi.totalMax;

    return {
      entry,
      foodMid,
      drive: { min: entry + transportEstimates.drive.total + foodEstimate.min, max: entry + transportEstimates.drive.total + foodEstimate.max, headline: driveTotal },
      taxi: { min: entry + taxiMinTransport + foodEstimate.min, max: entry + taxiMaxTransport + foodEstimate.max, headline: taxiBase },
      public: { min: entry + transportEstimates.public.total + foodEstimate.min, max: entry + transportEstimates.public.total + foodEstimate.max, headline: publicTotal }
    };
  }, [entryPricing.onlineTotal, foodEstimate.max, foodEstimate.min, transportEstimates.drive.total, transportEstimates.public.total, transportEstimates.taxi.cheapCabGrab, transportEstimates.taxi.live, transportEstimates.taxi.totalMax, transportEstimates.taxi.totalMin]);

  const foodHeadlineValue = useMemo(
    () =>
      foodEstimate.exact
        ? Number(foodEstimate.min || 0)
        : Math.round((Number(foodEstimate.min || 0) + Number(foodEstimate.max || 0)) / 2),
    [foodEstimate.exact, foodEstimate.max, foodEstimate.min]
  );
  const foodHeadlineText = `${money(foodHeadlineValue)} — ${foodEstimate.label}${foodEstimate.exact ? "" : " (modelled)"}`;

  const recommendation = useMemo(() => {
    if (!totalDayCosts) return [];
    const recs = [];
    const familyLoadHigh = trip.stroller || trip.elderly || trip.luggage === "heavy" || trip.tiredKids;

    if (totalDayCosts.drive.headline <= Math.min(totalDayCosts.taxi.headline, totalDayCosts.public.headline) - 15) {
      recs.push("💷 Best budget: Drive is currently the cheapest full-day option if you are happy to park and walk in.");
    }

    if (familyLoadHigh && transportEstimates.taxi.live) {
      recs.push("🚪 Lowest hassle: Taxi is strongest for strollers, elderly passengers, tired kids, or heavy bags because it avoids parking and station changes.");
    }

    if (
      transportEstimates.public.live &&
      !familyLoadHigh &&
      trip.luggage === "light" &&
      totalDayCosts.public.headline <= totalDayCosts.drive.headline + 20 &&
      transportEstimates.public.duration <= transportEstimates.drive.duration + 20
    ) {
      recs.push("🚆 Good public transport case: live rail is competitive here if you travel light and do not mind one station connection.");
    } else {
      recs.push("🚗 Parking note: self drive still gives the most control over return time and avoids needing a second paid leg home.");
    }

    if (trip.weatherSensitivity !== "low") {
      recs.push("🌧️ Weather watch: rain or cold increases walking hassle, which usually pushes taxi or self drive ahead of rail.");
    }

    return recs;
  }, [
    totalDayCosts,
    transportEstimates.drive.duration,
    transportEstimates.public.duration,
    transportEstimates.public.live,
    trip.elderly,
    trip.luggage,
    trip.stroller,
    trip.tiredKids,
    trip.weatherSensitivity
  ]);

  const modesToShow = trip.transport === "compare" ? ["drive", "taxi", "public"] : [trip.transport];

  const heroRecommendation = useMemo(() => {
    if (entryPricing.onlineTotal == null || !totalDayCosts) return null;
    const options = [
      {
        key: "drive",
        label: "Drive yourself",
        icon: "🚗",
        total: totalDayCosts.drive?.headline,
        range: totalDayCosts.drive ? rangeText(totalDayCosts.drive.min, totalDayCosts.drive.max) : "—",
        hassle: transportEstimates.drive?.hassle,
        duration: transportEstimates.drive?.duration,
        walk: transportEstimates.drive?.walkMins,
        why: "Cheapest and flexible return if you own the car."
      },
      {
        key: "taxi",
        label: "Taxi / Private Hire",
        icon: "🚕",
        total: totalDayCosts.taxi?.headline,
        range: totalDayCosts.taxi ? rangeText(totalDayCosts.taxi.min, totalDayCosts.taxi.max) : "—",
        hassle: transportEstimates.taxi?.hassle,
        duration: transportEstimates.taxi?.duration,
        walk: transportEstimates.taxi?.walkMins,
        why: "Zero parking or long walks; good with kids or luggage."
      },
      {
        key: "public",
        label: "Public transport",
        icon: "🚆",
        total: totalDayCosts.public?.headline,
        range: totalDayCosts.public ? rangeText(totalDayCosts.public.min, totalDayCosts.public.max) : "—",
        hassle: transportEstimates.public?.hassle,
        duration: transportEstimates.public?.duration,
        walk: transportEstimates.public?.walkMins,
        why: "Strong from London when live rail stays close on cost and you travel light."
      }
    ];
    const valid = options.filter((o) => Number.isFinite(o.total));
    if (!valid.length) return null;
    const cheapestTotal = Math.min(...valid.map((o) => o.total));
    const quickestDuration = Math.min(...valid.map((o) => Number(o.duration) || 999));
    const lowestHassle = Math.min(...valid.map((o) => Number(o.hassle?.score) || 999));
    const familyLoadHigh = trip.stroller || trip.elderly || trip.luggage === "heavy" || trip.tiredKids;

    const scored = valid.map((o) => {
      let score = 100;
      score -= Math.max(0, o.total - cheapestTotal) * 0.45;
      score -= Math.max(0, (Number(o.duration) || 0) - quickestDuration) * 0.18;
      score -= Math.max(0, (Number(o.hassle?.score) || 0) - lowestHassle) * 0.6;

      if (o.key === "drive") {
        score += 8;
        if (totalDayCosts.drive.headline <= totalDayCosts.public.headline + 20) score += 4;
      }

      if (o.key === "taxi") {
        score += 10;
        if (familyLoadHigh && transportEstimates.taxi.live) score += 10;
        if (trip.weatherSensitivity !== "low") score += 4;
        if (!transportEstimates.taxi.live) score -= 12;
      }

      if (o.key === "public") {
        if (transportEstimates.public.live) score += 6;
        if (!familyLoadHigh && trip.luggage === "light") score += 8;
        if (familyLoadHigh) score -= 14;
        if (!transportEstimates.public.live) score -= 6;
      }

      return { ...o, score };
    });

    scored.sort((a, b) => b.score - a.score || a.total - b.total);
    return scored[0];
  }, [entryPricing.onlineTotal, totalDayCosts, transportEstimates, trip.elderly, trip.luggage, trip.stroller, trip.tiredKids, trip.weatherSensitivity]);

  const transportTimelines = useMemo(() => {
    const openClock = dayData?.open_time || (trip.datetime ? String(trip.datetime).split("T")[1] : null) || "10:00";
    const openAt = parseClockDate(selectedDate, openClock);
    const targetGateArrival = subtractMinutes(openAt, 20) || parseClockDate(selectedDate, "09:40");
    const homeLabel = trip.pickup || "Home";

    const driveGate = targetGateArrival;
    const driveParking = subtractMinutes(driveGate, transportEstimates.drive.walkMins || 0);
    const driveLeave = subtractMinutes(driveParking, Math.max(0, (transportEstimates.drive.duration || 0) - (transportEstimates.drive.walkMins || 0)));

    const taxiArrive = targetGateArrival;
    const taxiDropoff = subtractMinutes(taxiArrive, transportEstimates.taxi.walkMins || 0);
    const taxiLeave = subtractMinutes(taxiDropoff, Math.max(0, (transportEstimates.taxi.duration || 0) - (transportEstimates.taxi.walkMins || 0)));

    let publicPark = targetGateArrival;
    const publicLastMile =
      (transportEstimates.public.lastMileTaxiDurationMins || 0) +
      Math.max(0, Number(liveRailPlan?.data?.last_mile_walk_mins || 0));
    const publicGateway = subtractMinutes(publicPark, publicLastMile);
    const bestDepart = transportEstimates.public.bestJourneyDepart ? new Date(transportEstimates.public.bestJourneyDepart) : null;
    const bestArrive = transportEstimates.public.bestJourneyArrive ? new Date(transportEstimates.public.bestJourneyArrive) : null;
    const trainLegMins =
      bestDepart && bestArrive && !Number.isNaN(bestDepart.getTime()) && !Number.isNaN(bestArrive.getTime())
        ? Math.max(0, Math.round((bestArrive.getTime() - bestDepart.getTime()) / 60000))
        : Math.max(0, (transportEstimates.public.duration || 0) - (transportEstimates.public.accessTaxiDurationMins || 0) - publicLastMile);
    const publicOrigin = subtractMinutes(publicGateway, trainLegMins);
    const accessToOriginMins =
      transportEstimates.public.accessMode === "taxi"
        ? transportEstimates.public.accessTaxiDurationMins || 0
        : Number(liveRailPlan?.data?.access_walk_mins || 0) || 12;
    const publicLeave = subtractMinutes(publicOrigin, accessToOriginMins);

    return {
      drive: {
        stops: [
          { time: formatTimelineClock(driveLeave), label: homeLabel },
          { time: formatTimelineClock(driveParking), label: "LEGOLAND parking" },
          { time: formatTimelineClock(driveGate), label: "LEGOLAND" },
        ],
      },
      taxi: {
        stops: [
          { time: formatTimelineClock(taxiLeave), label: homeLabel },
          { time: formatTimelineClock(taxiDropoff), label: "Taxi dropoff" },
          { time: formatTimelineClock(taxiArrive), label: "Legoland" },
        ],
      },
      public: {
        stops: [
          { time: formatTimelineClock(publicLeave), label: homeLabel },
          { time: formatTimelineClock(publicOrigin), label: transportEstimates.public.originName || stationLabelFromCrs(transportEstimates.public.originCrs, "Origin") },
          { time: formatTimelineClock(publicGateway), label: transportEstimates.public.gatewayName || stationLabelFromCrs(transportEstimates.public.gatewayCrs, "Gateway") },
          { time: formatTimelineClock(publicPark), label: "LEGOLAND" },
        ],
      },
    };
  }, [
    dayData?.open_time,
    liveRailPlan?.data?.access_walk_mins,
    liveRailPlan?.data?.last_mile_walk_mins,
    selectedDate,
    transportEstimates.drive.duration,
    transportEstimates.drive.walkMins,
    transportEstimates.public.accessMode,
    transportEstimates.public.accessTaxiDurationMins,
    transportEstimates.public.bestJourneyArrive,
    transportEstimates.public.bestJourneyDepart,
    transportEstimates.public.duration,
    transportEstimates.public.gatewayCrs,
    transportEstimates.public.gatewayName,
    transportEstimates.public.lastMileTaxiDurationMins,
    transportEstimates.public.originCrs,
    transportEstimates.public.originName,
    transportEstimates.taxi.duration,
    transportEstimates.taxi.walkMins,
    trip.datetime,
    trip.pickup,
  ]);

  const transportCards = useMemo(() => {
    if (!totalDayCosts) return [];
    return [
      {
        key: "drive",
        label: "Self drive",
        icon: "🚗",
        liveBadge: null,
        total: money(totalDayCosts.drive.headline),
        duration: `${transportEstimates.drive.duration} mins`,
        timeline: transportTimelines.drive,
        breakdownItems: [
          { label: "Tickets", value: money(entryPricing.onlineTotal) },
          { label: "Transport", value: money(transportEstimates.drive.total) },
          { label: "Food", value: money(foodHeadlineValue) }
        ],
        meta: `${transportEstimates.drive.walkMins} min walk • ${transportEstimates.drive.hassle.label}`,
        accent: heroRecommendation?.key === "drive"
          ? "linear-gradient(180deg, #fff3a6 0%, #ffd84d 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #f7fbff 100%)",
        borderColor: heroRecommendation?.key === "drive" ? "#d90f19" : "#dbe7f2",
        shadow: heroRecommendation?.key === "drive" ? "0 14px 30px rgba(217, 15, 25, 0.14)" : "0 6px 20px rgba(15,23,42,0.06)"
      },
      {
        key: "public",
        label: "Public transport",
        icon: "🚆",
        liveBadge: transportEstimates.public.live ? "Live" : null,
        total: money(totalDayCosts.public.headline),
        duration: `${transportEstimates.public.duration} mins`,
        timeline: transportTimelines.public,
        localLinkLabel: "View Slough / Heathrow bus options",
        breakdownItems: [
          { label: "Tickets", value: money(entryPricing.onlineTotal) },
          { label: "Transport", value: money(transportEstimates.public.total) },
          { label: "Food", value: money(foodHeadlineValue) }
        ],
        meta: `${transportEstimates.public.walkMins} min walk • ${changeCountText(transportEstimates.public.changes)} • ${transportEstimates.public.hassle.label}`,
        accent: heroRecommendation?.key === "public"
          ? "linear-gradient(180deg, #e6f6ff 0%, #c7e8ff 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #f2fbff 100%)",
        borderColor: heroRecommendation?.key === "public" ? "#1da1f2" : "#cfe8f7",
        shadow: heroRecommendation?.key === "public" ? "0 12px 28px rgba(29, 161, 242, 0.16)" : "0 8px 22px rgba(14,165,233,0.08)"
      },
      {
        key: "taxi",
        label: "Taxi",
        icon: "🚕",
        liveBadge: liveTaxiQuote?.oneWayFare != null || transportEstimates.taxi.live ? "Live" : null,
        total: money(totalDayCosts.taxi.headline),
        duration: `${transportEstimates.taxi.duration} mins`,
        timeline: transportTimelines.taxi,
        localLinkLabel: "View nearby live taxi fares",
        breakdownItems: [
          { label: "Tickets", value: money(entryPricing.onlineTotal) },
          { label: "Transport", value: money(transportEstimates.taxi.cheapCabGrab) },
          { label: "Food", value: money(foodHeadlineValue) }
        ],
        meta: `${transportEstimates.taxi.walkMins} min walk • ${transportEstimates.taxi.hassle.label}`,
        accent: heroRecommendation?.key === "taxi"
          ? "linear-gradient(180deg, #ffe8e5 0%, #ffd1c9 100%)"
          : "linear-gradient(180deg, #ffffff 0%, #fff7ef 100%)",
        borderColor: heroRecommendation?.key === "taxi" ? "#d90f19" : "#f8ddbc",
        shadow: heroRecommendation?.key === "taxi" ? "0 12px 28px rgba(217, 15, 25, 0.15)" : "0 8px 22px rgba(249,115,22,0.08)"
      }
    ];
  }, [entryPricing.onlineTotal, foodHeadlineValue, heroRecommendation?.key, liveTaxiQuote?.oneWayFare, totalDayCosts, transportEstimates, transportTimelines]);

  const openLocalTransportDetails = () => {
    setLocalTransportOpen(true);
    setTimeout(() => {
      localTransportRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 40);
  };

  const openNearbyTaxiQuote = async (point) => {
    if (!point || !apiTaxiBookableAt) return;
    setLocalTaxiBookingId(point.id);
    try {
      const res = await api.post("/api/get-quote-v2", {
        pickup: point.pickup,
        dropoff: DEST_POSTCODE,
        pickup_time: apiTaxiBookableAt,
        passengers: Math.min(Math.max(1, partySize || 1), 4),
        bags: Math.min(Math.max(0, Math.max((partySize || 1) - 1, 1)), 2),
      });
      const data = res.data || {};
      if (data.quote_id && data.access_token) {
        navigate(`/results/${data.quote_id}/${data.access_token}`);
      }
    } catch (err) {
    } finally {
      setLocalTaxiBookingId(null);
    }
  };

  const renderOption = (mode) => {
    if (mode === "drive") {
      return (
        <details className="parking-accordion" key="drive">
          <summary className="parking-accordion-summary">
            🚗 Drive yourself — Total day cost {money(totalDayCosts.drive.headline)} (includes tickets + food)
          </summary>
          <div className="train-collapsible-body">
            <div className="leave-grid">
              <div className="leave-grid-row">
                <div className="leave-grid-label">LEGOLAND entry</div>
                <div className="leave-grid-value">{money(entryPricing.onlineTotal)} (advance)</div>
              </div>
              <div className="leave-grid-row">
                <div className="leave-grid-label">Fuel estimate</div>
                <div className="leave-grid-value">{money(transportEstimates.drive.fuel)} round trip · {trip.carType.toUpperCase()}</div>
              </div>
              <div className="leave-grid-row">
                <div className="leave-grid-label">Parking</div>
                <div className="leave-grid-value">
                  {money(transportEstimates.drive.parking)} ({transportEstimates.drive.parkingType === "priority" ? "Priority" : transportEstimates.drive.parkingType === "disabled" ? "Disabled" : "Standard"} · {transportEstimates.drive.isWinter ? "winter rate" : "standard rate"})
                  <div className="parking-disclaimer" style={{ marginTop: 4 }}>
                    Standard: {money(parkingPrices.standard)} · Priority (closer): {money(parkingPrices.priority)} · EV charging requires priority. Disabled bays {money(parkingPrices.disabled)}, subject to availability.
                  </div>
                </div>
              </div>
              {transportEstimates.drive.ulez ? (
                <div className="leave-grid-row">
                  <div className="leave-grid-label">ULEZ / charges</div>
                  <div className="leave-grid-value">{money(transportEstimates.drive.ulez)} added for London zone</div>
                </div>
              ) : null}
              {transportEstimates.drive.wear ? (
                <div className="leave-grid-row">
                  <div className="leave-grid-label">Wear & tear</div>
                  <div className="leave-grid-value">{money(transportEstimates.drive.wear)} (optional)</div>
                </div>
              ) : null}
              <div className="leave-grid-row">
                <div className="leave-grid-label">⚡ EV drivers</div>
                <div className="leave-grid-value">34 charge points in Priority Car Park. Priority parking ticket required; charging paid separately via ChargePoint.</div>
              </div>
              <div className="leave-grid-row">
                <div className="leave-grid-label">Food plan</div>
                <div className="leave-grid-value">{foodHeadlineText}</div>
              </div>
              <div className="leave-grid-row">
                <div className="leave-grid-label">Time & effort</div>
                <div className="leave-grid-value">
                  {transportEstimates.drive.duration} mins door-to-gate · {transportEstimates.drive.walkMins} mins walk from car park · {trafficProfile.label}
                </div>
              </div>
              <div className="leave-grid-row">
                <div className="leave-grid-label">Hassle score</div>
                <div className="leave-grid-value">{transportEstimates.drive.hassle.label} ({transportEstimates.drive.hassle.score}/100)</div>
              </div>
              <div className="leave-grid-row">
                <div className="leave-grid-label">Notes</div>
                <div className="leave-grid-value">Queues for parking on busy days; arrive early to avoid back-up. Destination: {DEST_POSTCODE}.</div>
              </div>
            </div>
          </div>
        </details>
      );
    }

    if (mode === "taxi") {
      return (
        <details className="parking-accordion" key="taxi">
          <summary className="parking-accordion-summary">
            🚕 Taxi / Private Hire — Total day cost {money(totalDayCosts.taxi.headline)} (tickets + food + taxi)
          </summary>
          <div className="train-collapsible-body">
            <div className="leave-grid">
              <div className="leave-grid-row">
                <div className="leave-grid-label">LEGOLAND entry</div>
                <div className="leave-grid-value">{money(entryPricing.onlineTotal)} (advance)</div>
              </div>
              <div className="leave-grid-row">
                <div className="leave-grid-label">CheapCabGrab ({transportEstimates.taxi.live ? "live" : "fixed"})</div>
                <div className="leave-grid-value strong">
                  {money(transportEstimates.taxi.cheapCabGrab)} door-to-door
                  {transportEstimates.taxi.vehicle ? ` · ${String(transportEstimates.taxi.vehicle).toUpperCase()}` : ""}
                </div>
              </div>
              <div className="leave-grid-row">
                <div className="leave-grid-label">Uber estimate</div>
                <div className="leave-grid-value muted">{rangeText(transportEstimates.taxi.uber.min, transportEstimates.taxi.uber.max)}</div>
              </div>
              <div className="leave-grid-row">
                <div className="leave-grid-label">Black cab (metered)</div>
                <div className="leave-grid-value muted">{rangeText(transportEstimates.taxi.black.min, transportEstimates.taxi.black.max)}</div>
              </div>
              <div className="leave-grid-row">
                <div className="leave-grid-label">Food plan</div>
                <div className="leave-grid-value">{foodHeadlineText}</div>
              </div>
              <div className="leave-grid-row">
                <div className="leave-grid-label">Time & effort</div>
                <div className="leave-grid-value">
                  {transportEstimates.taxi.duration} mins door-to-gate · minimal walking ({transportEstimates.taxi.walkMins} mins) · {trafficProfile.label}
                </div>
              </div>
              <div className="leave-grid-row">
                <div className="leave-grid-label">Hassle score</div>
                <div className="leave-grid-value">{transportEstimates.taxi.hassle.label} ({transportEstimates.taxi.hassle.score}/100)</div>
              </div>
              <div className="leave-grid-row">
                <div className="leave-grid-label">Perks</div>
                <div className="leave-grid-value">No parking, no shuttle walk. Family-friendly cars; request seats when booking.</div>
              </div>
              <div className="leave-grid-row">
                <div className="leave-grid-label">Parking</div>
                <div className="leave-grid-value">
                  £0 (30-minute drop-off / pick-up included). Designated drop-off close to entrance; no exit queues and no walking from car park.
                </div>
              </div>
              <div className="leave-grid-row">
                <div className="leave-grid-label">Quote</div>
                <div className="leave-grid-value">
                  <a
                    href="/heathrow-taxi"
                    className="parking-btn"
                    style={{ display: "inline-block", width: "auto", textDecoration: "none", padding: "10px 14px" }}
                  >
                    Get a CheapCabGrab quote
                  </a>
                </div>
              </div>
            </div>
          </div>
        </details>
      );
    }

    return (
      <details className="parking-accordion" key="public">
        <summary className="parking-accordion-summary">
          🚆 Public Transport — Total day cost {money(totalDayCosts.public.headline)} (tickets + food + public transport)
        </summary>
        <div className="train-collapsible-body">
          <div
            className="best-choice"
            style={{
              background: transportEstimates.public.live ? "#ecfdf5" : "#fff7ed",
              borderLeft: transportEstimates.public.live ? "5px solid #22c55e" : "5px solid #fb923c",
            }}
          >
            {transportEstimates.public.live
              ? `Live National Rail route via ${transportEstimates.public.gatewayName || transportEstimates.public.gatewayCrs || "rail gateway"} with last-mile taxi to LEGOLAND.`
              : "National Rail live pricing is unavailable for this search, so we are showing a modelled public transport estimate."}
          </div>
          <div className="leave-grid">
            <div className="leave-grid-row">
              <div className="leave-grid-label">LEGOLAND entry</div>
              <div className="leave-grid-value">{money(entryPricing.onlineTotal)} (advance)</div>
            </div>
            <div className="leave-grid-row">
              <div className="leave-grid-label">{transportEstimates.public.live ? "Live National Rail fare" : "Rail fare (modelled)"}</div>
              <div className="leave-grid-value">
                {money(transportEstimates.public.railFare)} for {payingAdults + (trip.elderly ? 1 : 0)} adults & {payingChildren} {payingChildren === 1 ? "child" : "children"}
              </div>
            </div>
            {transportEstimates.public.live ? (
              <>
                <div className="leave-grid-row">
                  <div className="leave-grid-label">Rail route</div>
                  <div className="leave-grid-value">
                    {railRouteLine({
                      originName: transportEstimates.public.originName,
                      originCrs: transportEstimates.public.originCrs,
                      interchangesCrs: transportEstimates.public.interchangesCrs,
                      gatewayName: transportEstimates.public.gatewayName,
                      gatewayCrs: transportEstimates.public.gatewayCrs,
                    })}
                    {` · ${changeCountText(transportEstimates.public.changes)}`}
                  </div>
                </div>
                <div className="leave-grid-row">
                  <div className="leave-grid-label">First mile to origin station</div>
                  <div className="leave-grid-value">
                    {transportEstimates.public.accessMode === "walk"
                      ? `${transportEstimates.public.walkMins} mins walk to origin station`
                      : `${money(transportEstimates.public.accessTaxiGbp)} taxi (${transportEstimates.public.accessTaxiDurationMins} mins)`}
                  </div>
                </div>
                <div className="leave-grid-row">
                  <div className="leave-grid-label">Last mile from station to LEGOLAND</div>
                  <div className="leave-grid-value">
                    {money(transportEstimates.public.lastMileTaxiGbp)} taxi from {transportEstimates.public.gatewayName || transportEstimates.public.gatewayCrs || "gateway station"}
                    {transportEstimates.public.lastMileTaxiDurationMins ? ` · ${transportEstimates.public.lastMileTaxiDurationMins} mins` : ""}
                    {transportEstimates.public.lastMileTaxiGbp ? " (included in transport total)" : ""}
                  </div>
                </div>
              </>
            ) : (
              <>
                <div className="leave-grid-row">
                  <div className="leave-grid-label">TfL / bus add-on</div>
                  <div className="leave-grid-value">{money(transportEstimates.public.tflFare)} estimated (Tube + Windsor bus)</div>
                </div>
                <div className="leave-grid-row">
                  <div className="leave-grid-label">Shuttle bus</div>
                  <div className="leave-grid-value">{money(transportEstimates.public.shuttleBus)} (park shuttle not operated by LEGOLAND)</div>
                </div>
              </>
            )}
            <div className="leave-grid-row">
              <div className="leave-grid-label">Public transport total</div>
              <div className="leave-grid-value">
                {money(transportEstimates.public.total)}
                {transportEstimates.public.live
                  ? ` (${[
                      transportEstimates.public.railFare ? `${money(transportEstimates.public.railFare)} rail` : null,
                      transportEstimates.public.accessTaxiGbp ? `${money(transportEstimates.public.accessTaxiGbp)} first-mile taxi` : null,
                      transportEstimates.public.lastMileTaxiGbp ? `${money(transportEstimates.public.lastMileTaxiGbp)} last-mile taxi` : null,
                    ]
                      .filter(Boolean)
                      .join(" + ")})`
                  : ` (${[
                      transportEstimates.public.railFare ? `${money(transportEstimates.public.railFare)} rail` : null,
                      transportEstimates.public.tflFare ? `${money(transportEstimates.public.tflFare)} TfL / bus` : null,
                      transportEstimates.public.shuttleBus ? `${money(transportEstimates.public.shuttleBus)} shuttle` : null,
                    ]
                      .filter(Boolean)
                      .join(" + ")})`}
              </div>
            </div>
            <div className="leave-grid-row">
              <div className="leave-grid-label">Food plan</div>
              <div className="leave-grid-value">{foodHeadlineText}</div>
            </div>
            <div className="leave-grid-row">
              <div className="leave-grid-label">Time & effort</div>
                <div className="leave-grid-value">
                  {transportEstimates.public.duration} mins door-to-gate · {changeCountText(transportEstimates.public.changes)} · ~{transportEstimates.public.walkMins} mins walking
                </div>
            </div>
            <div className="leave-grid-row">
              <div className="leave-grid-label">Hassle score</div>
              <div className="leave-grid-value">{transportEstimates.public.hassle.label} ({transportEstimates.public.hassle.score}/100)</div>
            </div>
              <div className="leave-grid-row">
                <div className="leave-grid-label">Notes</div>
                <div className="leave-grid-value">
                  {transportEstimates.public.live
                    ? `Live route selected via ${transportEstimates.public.gatewayName || transportEstimates.public.gatewayCrs || "the chosen station gateway"}. ${transportEstimates.public.sourceNotes || "Last mile to LEGOLAND is priced as a taxi from the arrival station."}`
                    : "Public transport uses estimated rail plus local transfer time to LEGOLAND. Coach and Green Line options exist, but this planner scores the rail-based route for speed and flexibility."}
                </div>
              </div>
            <div className="parking-disclaimer" style={{ marginTop: 6 }}>
              {transportEstimates.public.live
                ? "Public transport now uses live National Rail fares where available, plus first-mile station access and last-mile taxi to LEGOLAND."
                : `Public transport costs (estimated). Rail fares shown are typical off-peak return prices based on distance, common routes, and advance availability. Distance used for estimate: ${distance} miles.`}
            </div>
          </div>
        </div>
      </details>
    );
  };

  return (
    <div className="parking-wrapper">
      <Helmet>
        <title>How Much Does LEGOLAND Windsor Cost? Live Planner 2026 — Your Total Day Cost</title>
        <meta
          name="description"
          content="Enter your postcode to see your exact LEGOLAND day cost today. Live train fares, live taxi prices, real parking and ticket costs. Know your total cost and when to leave before you book."
        />
        <meta
          name="keywords"
          content="LEGOLAND Windsor budget planner, Legoland cost calculator, how much does Legoland Windsor cost, Legoland tickets, Legoland parking cost, Legoland taxi cost, Legoland train cost, Legoland live rail fares, taxi to Legoland from Slough station, Heathrow to Legoland, Slough to Legoland taxi, Windsor to Legoland taxi, Legoland hotels, Legoland Resort Hotel, Legoland Castle Hotel, Woodland Village, Legoland Merlin buffet price, Legoland opening times, is Legoland open today, Legoland weather, train vs taxi to Legoland, cheapest way to Legoland"
        />
        <link rel="canonical" href="https://www.cheapcabgrab.com/legoland-budget-planner" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://www.cheapcabgrab.com/legoland-budget-planner" />
        <meta property="og:title" content="LEGOLAND Windsor Budget Planner" />
        <meta
          property="og:description"
          content="Not sure whether to drive, get the train or take a taxi? See your total LEGOLAND day cost by postcode with live prices, real ticket costs and exact time to leave. Updated daily."
        />
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content="LEGOLAND Windsor Budget Planner" />
        <meta name="twitter:description" content="Compare LEGOLAND tickets, parking, live taxi fares, live rail fares, hotels, food, weather and leave-home timing from your postcode." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "SoftwareApplication",
            name: "Legoland Windsor Budget Planner",
            applicationCategory: "TravelApplication",
            operatingSystem: "Web",
            url: "https://www.cheapcabgrab.com/legoland-budget-planner",
            offers: {
              "@type": "Offer",
              price: "0",
              priceCurrency: "GBP"
            },
            featureList: [
              "Live total day cost for drive, train and taxi from your postcode",
              "Real LEGOLAND ticket prices checked daily",
              "Live National Rail fares and departure times",
              "Live taxi fares to LEGOLAND from your location",
              "Exact time to leave home for each travel mode",
              "Real parking costs for your visit date"
            ]
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "TouristAttraction",
            name: "LEGOLAND Windsor Resort",
            address: {
              "@type": "PostalAddress",
              streetAddress: "Winkfield Road",
              addressLocality: "Windsor",
              postalCode: "SL4 4AY",
              addressCountry: "UK"
            },
            url: "https://www.cheapcabgrab.com/legoland-budget-planner"
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: [
              {
                "@type": "Question",
                name: "Is parking free at Legoland Windsor?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No. Standard parking costs £12 and priority costs £18 (winter rates lower). Parking queues can be long on busy days; taxis avoid parking and the 9-minute walk."
                }
              },
              {
                "@type": "Question",
                name: "Can I use my parking booking if I bring a different car?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. Your parking barcode should still work at the barrier even if the registration is different or was not entered. Registration may be used for convenience and can help with a faster exit, but entry is mainly via barcode."
                }
              },
              {
                "@type": "Question",
                name: "What are the main ways to get discounted LEGOLAND tickets?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "The main discount routes people usually check are advance online booking, Sky VIP offers, Tesco Clubcard rewards, Blue Light or NHS discounts, and promotional codes from brands like Kellogg's or Cadbury. Some offers are fixed-price deals, while others are based on full gate prices, so the real saving can vary by date."
                }
              },
              {
                "@type": "Question",
                name: "Is there a train station at Legoland Windsor?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No. The nearest stations are Windsor & Eton Central, Windsor & Eton Riverside, or Slough depending on the route. You still need a last-mile transfer from the station to the park, and the planner includes first-mile station access and last-mile taxi where needed."
                }
              },
              {
                "@type": "Question",
                name: "Is public transport cheaper than a taxi to Legoland?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Not always. For families of 3–4, train + bus costs can exceed a fixed-price taxi once walking time and changes are included. The planner compares both for your date."
                }
              },
              {
                "@type": "Question",
                name: "Do Legoland ticket prices change every day?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. Legoland ticket prices vary by date and demand. The planner uses the latest available 'from' price per date and shows cheaper days where possible."
                }
              },
              {
                "@type": "Question",
                name: "What does the LEGOLAND budget planner include?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "It includes live taxi fares where available, live National Rail fares where available, real parking prices, opening hours, ticket prices, leave-home to arrival timings, first-mile station access, last-mile transfers, walking time, changes, hassle score, and a weather forecast for your visit when available."
                }
              },
              {
                "@type": "Question",
                name: "What is the LEGOLAND Merlin buffet price?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "The Pizza and Pasta buffet with a Merlin Pass is typically £16.75 per adult and £10 per child, usually including a refill soft drink, but prices and locations can change."
                }
              },
              {
                "@type": "Question",
                name: "How much is the LEGOLAND Driving School licence souvenir?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "The photo driving licence souvenir is usually about £11.99 online including VAT and around £13.99 in the park. Children aged 6 to 13 and roughly 1.1m to 1.5m can drive the cars on the attraction itself, but the printed licence photo souvenir costs extra."
                }
              },
              {
                "@type": "Question",
                name: "How do I get from Slough or Heathrow to LEGOLAND?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Many visitors use the 702 or 703 buses from Slough, Windsor, or Heathrow Terminal 5 to LEGOLAND Park and Ride, while others prefer a taxi from Slough Station or Heathrow for a simpler last mile. This planner now includes a collapsible section with bus guidance and live taxi preview fares from key nearby points."
                }
              },
              {
                "@type": "Question",
                name: "Do children under 90cm enter LEGOLAND free?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Usually yes, children under 90cm can enter free. At 97cm you should expect to need a paid ticket, and staff can be strict on height checks."
                }
              },
              {
                "@type": "Question",
                name: "Is LEGOLAND good for younger children?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "Yes. LEGOLAND is strongest for younger children and primary-age families, especially around ages 2 to 10, with lots of gentle rides, themed play areas, and family attractions."
                }
              },
              {
                "@type": "Question",
                name: "Are LEGOLAND hotels worth it?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "They are mainly worth it for convenience and the kid-focused experience. Resort Hotel is usually the strongest all-round choice, while Woodland Village can be a good summer option, but hotel guests should expect some activities like swimming or dining slots to need advance booking."
                }
              },
              {
                "@type": "Question",
                name: "Is Legoland Windsor open every day?",
                acceptedAnswer: {
                  "@type": "Answer",
                  text: "No. Legoland runs a seasonal calendar and closes on some weekdays outside peak periods. Check the opening times for your selected date before travelling."
                }
              }
            ]
          })}
        </script>
      </Helmet>

      <LiveStrip variant="legoland" />

      <div className="parking-header" style={isMobile ? { marginTop: 6, marginBottom: 8 } : undefined}>
        <h1 style={{ fontSize: isMobile ? 24 : undefined, lineHeight: isMobile ? 1.04 : undefined, marginBottom: isMobile ? 8 : undefined }}>
          🎢 Legoland Windsor Travel & Budget Planner: Compare Taxi, Train, and Driving Costs
        </h1>
        <div style={{ fontSize: 13, color: "#475467", marginTop: isMobile ? 0 : 4, lineHeight: isMobile ? 1.32 : 1.5 }}>
          <div style={{ fontSize: 11, color: "#6b7280", lineHeight: isMobile ? 1.3 : undefined }}>
            Live taxi pricing and live National Rail fares are now included where available. Drive pricing uses current planner assumptions. Updated: 12th May 2026
          </div>
          <div style={{ fontSize: isMobile ? 11 : 12, marginTop: isMobile ? 1 : 0, lineHeight: isMobile ? 1.32 : undefined }}>
            One screen to price your family day: tickets, transport, food, time, weather, and hassle — tailored to {DEST_POSTCODE}.
          </div>
          <div style={{ fontSize: isMobile ? 12 : 14, fontWeight: 700, marginTop: isMobile ? 3 : 4, lineHeight: isMobile ? 1.28 : undefined }}>
            Pick your date and postcode to see which route is cheaper, faster, and easier for your family in 2026.
          </div>
        </div>
      </div>

      {isUpdating && (
        <div
          style={{
            display: "inline-flex",
            alignItems: "center",
            gap: 6,
            padding: "6px 10px",
            marginTop: 6,
            borderRadius: 999,
            background: "#e0f2fe",
            border: "1px solid #bae6fd",
            color: "#0f172a",
            fontSize: 12,
            boxShadow: "0 6px 14px rgba(0,0,0,0.06)"
          }}
        >
          <span role="img" aria-label="updating">⏳</span>
          <span>Updating planner…</span>
        </div>
      )}

      <div className="parking-form" style={isMobile ? { marginTop: 8, paddingTop: 12, paddingBottom: 12, background: "linear-gradient(180deg, #ffffff 0%, #fffdf5 100%)", border: "1px solid #efe4b8", boxShadow: "0 14px 28px rgba(15, 98, 182, 0.05)" } : { background: "linear-gradient(180deg, #ffffff 0%, #fffdf5 100%)", border: "1px solid #efe4b8", boxShadow: "0 14px 28px rgba(15, 98, 182, 0.05)" }}>
        <div
          className="option-grid"
          style={{ gap: isMobile ? 10 : 12, alignItems: "flex-start", gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))" }}
        >
          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: isMobile ? "6px 11px" : "7px 13px",
                  borderRadius: 999,
                  background: "linear-gradient(180deg, #fff7cc 0%, #ffe082 100%)",
                  color: "#9a3412",
                  fontSize: isMobile ? 12 : 13,
                  fontWeight: 800,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                }}
              >
                Enter postcode
              </span>
            </label>
            <div style={{ position: "relative" }}>
              <input
                className="parking-input"
                value={formTrip.pickup}
                onChange={(e) => {
                  triggerUpdateFlash("pickup");
                  setFormTrip({ ...formTrip, pickup: e.target.value });
                  setShowPostcodeSuggestions(normalizePostcode(e.target.value).length >= 3);
                }}
                onFocus={() => {
                  setShowPickupNudge(false);
                  if (visiblePostcodeSuggestions.length) setShowPostcodeSuggestions(true);
                }}
                onBlur={() => {
                  setTimeout(() => setShowPostcodeSuggestions(false), 120);
                }}
                placeholder="e.g. SW1A 1AA"
                autoComplete="postal-code"
                style={{
                  ...heroInputStyle,
                  border: showPickupNudge ? "1px solid #fbbf24" : heroInputStyle.border,
                  boxShadow: showPickupNudge
                    ? "0 0 0 4px rgba(251,191,36,0.16), inset 0 1px 0 rgba(255,255,255,0.75), 0 8px 18px rgba(245,158,11,0.10)"
                    : heroInputStyle.boxShadow,
                }}
              />
              {showPostcodeSuggestions && visiblePostcodeSuggestions.length > 0 && (
                <div
                  style={{
                    position: "absolute",
                    top: "calc(100% + 6px)",
                    left: 0,
                    right: 0,
                    zIndex: 20,
                    background: "#ffffff",
                    border: "1px solid #dbe4f0",
                    borderRadius: 14,
                    boxShadow: "0 16px 30px rgba(15,23,42,0.12)",
                    overflow: "hidden"
                  }}
                >
                  {visiblePostcodeSuggestions.slice(0, 6).map((postcode, idx) => (
                    <button
                      key={postcode}
                      type="button"
                      onMouseDown={(e) => e.preventDefault()}
                      onClick={() => handlePostcodeSuggestionPick(postcode)}
                      style={{
                        display: "block",
                        width: "100%",
                        textAlign: "left",
                        padding: isMobile ? "10px 12px" : "10px 12px",
                        background: "#ffffff",
                        border: "none",
                        borderTop: idx === 0 ? "none" : "1px solid #eef2f7",
                        fontSize: isMobile ? 15 : 14,
                        color: "#0f172a"
                      }}
                    >
                      {postcode}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div style={{ fontSize: isMobile ? 10.5 : 12, color: "#6b7280", marginTop: 3, lineHeight: 1.28 }}>
              Destination: {DEST_POSTCODE}
              {postcodeLookupLoading ? " • checking postcode…" : ""}
              {!postcodeLookupLoading && visiblePostcodeSuggestions.length > 0 ? ` • ${visiblePostcodeSuggestions.length} suggestions` : ""}
            </div>
            {showPickupNudge && !refreshFlash && (
              <div style={{ fontSize: 11.5, color: "#9a3412", marginTop: 4, lineHeight: 1.3 }}>
                Enter your postcode to see your personal journey planner, live fares and travel times.
              </div>
            )}
            {refreshFlash && lastChangedField === "pickup" && (
              <div style={{ fontSize: 11, color: "#0f172a", marginTop: 4 }}>Updating planner…</div>
            )}
            {postcodeError && (
              <div style={{ fontSize: 12, color: "#b91c1c", marginTop: 4 }}>{postcodeError}</div>
            )}
          </div>

          <div className="form-group" style={{ marginBottom: 0 }}>
            <label style={{ display: "flex", alignItems: "center", gap: 8, flexWrap: "wrap" }}>
              <span
                style={{
                  display: "inline-flex",
                  alignItems: "center",
                  padding: isMobile ? "6px 11px" : "7px 13px",
                  borderRadius: 999,
                  background: "linear-gradient(180deg, #eff6ff 0%, #dbeafe 100%)",
                  color: "#1d4ed8",
                  fontSize: isMobile ? 12 : 13,
                  fontWeight: 800,
                  letterSpacing: 0.3,
                  textTransform: "uppercase",
                }}
              >
                Visiting date
              </span>
            </label>
            <input
              type="date"
              className="parking-input"
              value={formTrip.datetime ? formTrip.datetime.split("T")[0] : FALLBACK_LEGO_DATE}
              onChange={(e) => {
                triggerUpdateFlash("date");
                setFormTrip({ ...formTrip, datetime: `${e.target.value}T10:00` });
              }}
              style={heroInputStyle}
            />
            <div style={{ fontSize: isMobile ? 10.5 : 12, color: "#6b7280", marginTop: 3, lineHeight: 1.28 }}>
              We use a 10:00 park arrival for pricing and traffic.
            </div>
            {refreshFlash && lastChangedField === "date" && (
              <div style={{ fontSize: 11, color: "#0f172a", marginTop: 4 }}>Updating planner…</div>
            )}
          </div>
        </div>

        <details className="train-collapsible" style={{ marginTop: isMobile ? 10 : 12 }}>
          <summary className="train-summary-bar" style={isMobile ? { padding: "10px 12px", fontSize: 15, lineHeight: 1.28, background: "linear-gradient(180deg, #fff9ea 0%, #f8fbff 100%)", border: "1px solid #e7dcc0" } : { background: "linear-gradient(180deg, #fff9ea 0%, #f8fbff 100%)", border: "1px solid #e7dcc0" }}>
            ⚙️ More trip details (optional)
            <span style={{ display: "block", fontSize: isMobile ? 11 : 12, fontWeight: 400, opacity: 0.8, marginTop: 2, lineHeight: isMobile ? 1.28 : undefined }}>
              {hiddenOptionsSummary}
            </span>
          </summary>
          <div className="train-collapsible-body">
            <div className="form-group" style={{ marginBottom: 12 }}>
              <label className="option-pill return-pill" style={{ marginTop: 0, display: "inline-flex", padding: isMobile ? "6px 10px" : "8px 12px" }}>
                <input
                  type="checkbox"
                  checked={formTrip.returnSame}
                  onChange={(e) => {
                    triggerUpdateFlash("returnSame");
                    setFormTrip({ ...formTrip, returnSame: e.target.checked });
                  }}
                />
                <span>Return same day</span>
              </label>
              {refreshFlash && lastChangedField === "returnSame" && (
                <div style={{ fontSize: 11, color: "#0f172a", marginTop: 4 }}>Updating planner…</div>
              )}
            </div>

            <div className="option-grid compact-two" style={{ gap: 10, alignItems: "flex-end" }}>
              <div className="form-group" style={{ marginBottom: 8 }}>
                <label>Adults</label>
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  max="6"
                  className="parking-input"
                  style={isMobile ? { minHeight: 44, padding: "8px 12px", fontSize: 16 } : undefined}
                  value={clampNumber(formTrip.adults, 0, 6, 0)}
                  onChange={(e) => {
                    triggerUpdateFlash("adults");
                    setFormTrip({ ...formTrip, adults: clampNumber(e.target.value, 0, 6, 0) });
                  }}
                />
                {refreshFlash && lastChangedField === "adults" && (
                  <div style={{ fontSize: 11, color: "#0f172a", marginTop: 2 }}>Updating planner…</div>
                )}
              </div>
              <div className="form-group" style={{ marginBottom: 8 }}>
                <label>Children (90cm+)</label>
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  max="6"
                  className="parking-input"
                  style={isMobile ? { minHeight: 44, padding: "8px 12px", fontSize: 16 } : undefined}
                  value={clampNumber(formTrip.children, 0, 6, 0)}
                  onChange={(e) => {
                    triggerUpdateFlash("children");
                    setFormTrip({ ...formTrip, children: clampNumber(e.target.value, 0, 6, 0) });
                  }}
                />
                {refreshFlash && lastChangedField === "children" && (
                  <div style={{ fontSize: 11, color: "#0f172a", marginTop: 2 }}>Updating planner…</div>
                )}
              </div>
              <div className="form-group" style={{ marginBottom: 8 }}>
                <label>Under 90cm (free)</label>
                <input
                  type="number"
                  inputMode="numeric"
                  min="0"
                  max="6"
                  className="parking-input"
                  style={isMobile ? { minHeight: 44, padding: "8px 12px", fontSize: 16 } : undefined}
                  value={clampNumber(formTrip.under90, 0, 6, 0)}
                  onChange={(e) => {
                    triggerUpdateFlash("under90");
                    setFormTrip({ ...formTrip, under90: clampNumber(e.target.value, 0, 6, 0) });
                  }}
                />
                {refreshFlash && lastChangedField === "under90" && (
                  <div style={{ fontSize: 11, color: "#0f172a", marginTop: 2 }}>Updating planner…</div>
                )}
              </div>
              <div className="form-group" style={{ marginBottom: 8 }}>
                <label>Luggage</label>
                <select
                  className="parking-input"
                  style={isMobile ? { minHeight: 44, padding: "8px 12px", fontSize: 16 } : undefined}
                  value={formTrip.luggage}
                  onChange={(e) => {
                    triggerUpdateFlash("luggage");
                    setFormTrip({ ...formTrip, luggage: e.target.value });
                  }}
                >
                  <option value="none">None</option>
                  <option value="light">Light (backpacks)</option>
                  <option value="heavy">Heavy (several bags)</option>
                </select>
                {refreshFlash && lastChangedField === "luggage" && (
                  <div style={{ fontSize: 11, color: "#0f172a", marginTop: 2 }}>Updating planner…</div>
                )}
              </div>
            </div>

            <div className="form-group" style={{ marginTop: 6 }}>
              <label>Travel choice</label>
              <select
                className="parking-input"
                style={isMobile ? { minHeight: 44, padding: "8px 12px", fontSize: 16 } : undefined}
                value={formTrip.transport}
                onChange={(e) => {
                  triggerUpdateFlash("transport");
                  setFormTrip({ ...formTrip, transport: e.target.value });
                }}
              >
                <option value="compare">Compare all</option>
                <option value="drive">Drive</option>
                <option value="taxi">Taxi</option>
                <option value="public">Public transport</option>
              </select>
              {refreshFlash && lastChangedField === "transport" && (
                <div style={{ fontSize: 11, color: "#0f172a", marginTop: 2 }}>Updating planner…</div>
              )}
              <div style={{ marginTop: 6, color: "#6b7280", fontSize: 12 }}>
                Distance: {distance} miles{distanceStatus ? `, via ${distanceStatus}` : ""}.{" "}
                {distanceError ? <span style={{ color: "#b45309" }}>{distanceError}</span> : "Adjust in assumptions if needed."}
              </div>
            </div>

            <div className="form-group" style={{ marginTop: 10 }}>
              <label>Food</label>
              <div className="food-pills" style={{ gap: 6 }}>
                {[
                  { key: "packed", title: "Packed" },
                  { key: "mixed", title: "Mixed" },
                  { key: "eat", title: "Eat at park" },
                  { key: "buffet", title: "Buffet (Merlin)" }
                ].map((opt) => (
                  <label key={opt.key} className="food-pill" style={{ padding: isMobile ? "5px 8px" : "6px 10px", fontSize: isMobile ? 13 : undefined }}>
                    <input
                      type="radio"
                      name="food"
                      checked={formTrip.foodChoice === opt.key}
                      onChange={() => {
                        triggerUpdateFlash("food");
                        setFormTrip({ ...formTrip, foodChoice: opt.key, snackStop: opt.key === "buffet" ? false : formTrip.snackStop });
                      }}
                    />
                    <span>{opt.title}</span>
                  </label>
                ))}
                <label className="food-pill" style={{ padding: isMobile ? "5px 8px" : "6px 10px", fontSize: isMobile ? 13 : undefined }}>
                  <input
                    type="checkbox"
                    checked={formTrip.snackStop}
                    disabled={formTrip.foodChoice === "buffet"}
                    onChange={(e) => {
                      triggerUpdateFlash("food");
                      setFormTrip({ ...formTrip, snackStop: e.target.checked });
                    }}
                  />
                  <span>+ snack</span>
                </label>
              </div>
              <div style={{ marginTop: 6, fontSize: 12, color: "#6b7280", lineHeight: 1.35 }}>
                {formTrip.foodChoice === "buffet"
                  ? `Buffet (Merlin): ${money(LEGOLAND_BUFFET_MERLIN.adult, { decimals: 2 })} per adult and ${money(LEGOLAND_BUFFET_MERLIN.child)} per child, usually including a refill soft drink.`
                  : "Food remains estimated unless you choose the Merlin buffet option."}
              </div>
              {refreshFlash && lastChangedField === "food" && (
                <div style={{ fontSize: 11, color: "#0f172a", marginTop: 4 }}>Updating planner…</div>
              )}
            </div>

            <div className="option-grid">
              <div className="form-group">
                <label>Car type</label>
                <select
                  className="parking-input"
                  style={isMobile ? { minHeight: 44, padding: "8px 12px", fontSize: 16 } : undefined}
                  value={formTrip.carType}
                  onChange={(e) => setFormTrip({ ...formTrip, carType: e.target.value })}
                >
                  <option value="petrol">Petrol</option>
                  <option value="diesel">Diesel</option>
                  <option value="ev">EV</option>
                </select>
              </div>
              <div className="form-group">
                <label>MPG</label>
                <input
                  type="number"
                  className="parking-input"
                  style={isMobile ? { minHeight: 44, padding: "8px 12px", fontSize: 16 } : undefined}
                  value={formTrip.mpg}
                  onChange={(e) => setFormTrip({ ...formTrip, mpg: e.target.value })}
                />
              </div>
              <div className="form-group">
                <label>Fuel / kWh price (£)</label>
                <input
                  type="number"
                  className="parking-input"
                  style={isMobile ? { minHeight: 44, padding: "8px 12px", fontSize: 16 } : undefined}
                  value={formTrip.fuelPrice}
                  onChange={(e) => setFormTrip({ ...formTrip, fuelPrice: e.target.value })}
                  step="0.01"
                />
              </div>
            </div>

            <div className="option-grid" style={{ marginTop: 10 }}>
              <div className="form-group">
                <label>Parking choice</label>
                <select
                  className="parking-input"
                  style={isMobile ? { minHeight: 44, padding: "8px 12px", fontSize: 16 } : undefined}
                  value={formTrip.parkingType}
                  onChange={(e) => setFormTrip({ ...formTrip, parkingType: e.target.value })}
                  disabled={formTrip.carType === "ev"}
                >
                  <option value="standard">Standard</option>
                  <option value="priority">Priority (closer)</option>
                  <option value="disabled">Disabled</option>
                </select>
                <div style={{ fontSize: isMobile ? 11 : 12, color: "#6b7280", marginTop: 4, lineHeight: 1.35 }}>
                  Standard £{parkingPrices.standard} • Priority £{parkingPrices.priority}. EV charging requires priority. Disabled £{parkingPrices.disabled}.
                </div>
              </div>
              <div className="form-group">
                <label>Distance to LEGOLAND (one-way, miles)</label>
                <input
                  type="number"
                  min="1"
                  max="180"
                  className="parking-input"
                  style={isMobile ? { minHeight: 44, padding: "8px 12px", fontSize: 16 } : undefined}
                  value={clampNumber(formTrip.distanceMiles, 1, 180, 25)}
                  onChange={(e) => setFormTrip({ ...formTrip, distanceMiles: e.target.value })}
                />
              </div>
            </div>

            <div className="option-grid" style={{ marginTop: 10 }}>
              <div className="form-group">
                <label>Walking tolerance</label>
                <select
                  className="parking-input"
                  style={isMobile ? { minHeight: 44, padding: "8px 12px", fontSize: 16 } : undefined}
                  value={formTrip.walkingTolerance}
                  onChange={(e) => setFormTrip({ ...formTrip, walkingTolerance: e.target.value })}
                >
                  <option value="high">High</option>
                  <option value="medium">Medium</option>
                  <option value="low">Low</option>
                </select>
              </div>
              <div className="form-group">
                <label>Weather sensitivity</label>
                <select
                  className="parking-input"
                  style={isMobile ? { minHeight: 44, padding: "8px 12px", fontSize: 16 } : undefined}
                  value={formTrip.weatherSensitivity}
                  onChange={(e) => setFormTrip({ ...formTrip, weatherSensitivity: e.target.value })}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                </select>
                <div style={{ fontSize: isMobile ? 11 : 12, color: "#475467", marginTop: 4, lineHeight: 1.35 }}>
                  Weather expected: {seasonalWeather.label}. {seasonalWeather.note}
                </div>
              </div>
            </div>

            <div className="option-grid" style={{ marginTop: 10 }}>
              <div className="form-group">
                <label>Charges</label>
                <div className="option-list">
                  <label className="option-pill">
                    <input
                      type="checkbox"
                      checked={formTrip.ulez}
                      onChange={(e) => setFormTrip({ ...formTrip, ulez: e.target.checked })}
                    />
                    <span>Include ULEZ / urban charges</span>
                  </label>
                  <label className="option-pill">
                    <input
                      type="checkbox"
                      checked={formTrip.includeWearTear}
                      onChange={(e) => setFormTrip({ ...formTrip, includeWearTear: e.target.checked })}
                    />
                    <span>Include wear & tear (15p per mile)</span>
                  </label>
                </div>
              </div>
              <div className="form-group">
                <label>Family comfort</label>
                <div className="option-list">
                  <label className="option-pill">
                    <input
                      type="checkbox"
                      checked={formTrip.elderly}
                      onChange={(e) => setFormTrip({ ...formTrip, elderly: e.target.checked })}
                    />
                    <span>Elderly / reduced mobility</span>
                  </label>
                  <label className="option-pill">
                    <input
                      type="checkbox"
                      checked={formTrip.stroller}
                      onChange={(e) => setFormTrip({ ...formTrip, stroller: e.target.checked })}
                    />
                    <span>Pushchair / stroller</span>
                  </label>
                  <label className="option-pill">
                    <input
                      type="checkbox"
                      checked={formTrip.tiredKids}
                      onChange={(e) => setFormTrip({ ...formTrip, tiredKids: e.target.checked })}
                    />
                    <span>Tired kids</span>
                  </label>
                </div>
              </div>
            </div>
          </div>
        </details>

        <div style={{ display: "flex", alignItems: "center", gap: isMobile ? 10 : 12, flexWrap: "wrap", marginTop: isMobile ? 10 : 14 }}>
          <button
            type="button"
            className="parking-btn"
            onClick={applyPlannerInputs}
            style={{
              width: "auto",
              minWidth: isMobile ? 0 : 180,
              padding: isMobile ? "8px 12px" : undefined,
              fontSize: isMobile ? 14 : undefined,
              cursor: "pointer",
              background: hasPendingChanges ? undefined : "linear-gradient(180deg, #ffd54f 0%, #fbbf24 100%)",
              color: hasPendingChanges ? undefined : "#1f2937",
              boxShadow: hasPendingChanges ? undefined : "0 10px 24px rgba(245,158,11,0.24)",
              borderRadius: isMobile ? 12 : undefined
            }}
          >
            {hasPendingChanges ? "Show my Legoland plan" : "Refresh planner"}
          </button>
          {hasPendingChanges && (
            <div style={{ fontSize: isMobile ? 12 : 13, color: "#475467", lineHeight: 1.35 }}>
              Inputs changed. Apply to refresh results below.
            </div>
          )}
        </div>
      </div>

      {!entryPricing.onlineTotal && (
        <div className="parking-disclaimer" style={{ marginTop: 10, padding: 12, border: "1px solid #f59e0b", background: "#fffbeb" }}>
          No live LEGOLAND ticket or opening data for this date. Assuming closed; travel costs shown without tickets.
        </div>
      )}

      <div style={{ marginTop: isMobile ? 10 : 14 }}>
        <h2 style={{ margin: 0, fontSize: isMobile ? 20 : 24, lineHeight: isMobile ? 1.1 : 1.2 }}>Your Legoland day plan</h2>
      </div>

      <div
        className="parking-results"
        style={{
          marginTop: 12,
          padding: isMobile ? 14 : 20,
          borderRadius: 20,
          background: "linear-gradient(180deg, #fffef8 0%, #f2f9ff 100%)",
          border: "1px solid #d9e7f6",
          boxShadow: "0 12px 30px rgba(0, 87, 184, 0.08)"
        }}
      >
        {heroRecommendation && (
          <div
            style={{
              padding: isMobile ? 12 : 16,
              borderRadius: 18,
              background: "linear-gradient(135deg, #0057b8 0%, #0b74d1 52%, #2d9cff 100%)",
              color: "#ffffff",
              border: "1px solid #0f62b6",
              boxShadow: "inset 0 1px 0 rgba(255,255,255,0.08), 0 12px 28px rgba(0, 87, 184, 0.24)",
              position: "relative",
              overflow: "hidden"
            }}
          >
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                inset: 0,
                background: "linear-gradient(110deg, rgba(255,214,10,0.22) 0%, rgba(255,214,10,0.08) 24%, rgba(217,15,25,0.20) 68%, rgba(217,15,25,0.10) 100%)",
                pointerEvents: "none"
              }}
            />
            <div
              aria-hidden="true"
              style={{
                position: "absolute",
                top: 0,
                right: 0,
                width: isMobile ? 44 : 64,
                height: isMobile ? 44 : 64,
                background: "#d90f19",
                clipPath: "polygon(100% 0, 0 0, 100% 100%)",
                opacity: 0.92,
                pointerEvents: "none"
              }}
            />
            <div style={{ position: "relative", fontSize: 12, textTransform: "uppercase", letterSpacing: 0.6, color: "rgba(255,255,255,0.76)" }}>
              {dayData?.is_open ? "Park status" : "Selected date"}
            </div>
            {dayData?.is_open ? (
              <>
                <div style={{ position: "relative", marginTop: 2, fontSize: isMobile ? 17 : 24, fontWeight: 800, lineHeight: isMobile ? 1.08 : 1.12 }}>
                  LEGOLAND Windsor is open on {formattedSelectedDate || selectedDate || "your date"}
                </div>
                <div style={{ position: "relative", marginTop: 3, display: "flex", flexWrap: "wrap", gap: isMobile ? 4 : 10, fontSize: isMobile ? 11.5 : 14, color: "rgba(255,255,255,0.92)", lineHeight: 1.24 }}>
                  <span>
                    {dayData?.open_time && dayData?.close_time
                      ? `Open ${formatTime(dayData.open_time)}-${formatTime(dayData.close_time)}`
                      : "Hours unavailable"}
                  </span>
                  <span aria-hidden="true">•</span>
                  <span>From {dayData?.price_from_gbp ? money(dayData.price_from_gbp, { decimals: 2 }) : "—"} per ticket</span>
                  {dayData?.price_age_days != null && (
                    <>
                      <span aria-hidden="true">•</span>
                      <span>
                        Checked today
                      </span>
                    </>
                  )}
                </div>
                <div style={{ position: "relative", marginTop: 6, fontSize: isMobile ? 10 : 12, color: "rgba(255,255,255,0.78)", lineHeight: 1.24 }}>
                  ({formattedSelectedDate || "Pick a date"} • {trip.pickup || "add postcode"}
                  {partySummary ? ` • ${partySummary}` : ""})
                </div>
                <div style={{ position: "relative", marginTop: 8, fontSize: isMobile ? 10.5 : 12, textTransform: "uppercase", letterSpacing: 0.6, color: "#ffe082" }}>
                  Recommendation
                </div>
                <div style={{ position: "relative", marginTop: 2, fontSize: isMobile ? 16 : 20, fontWeight: 800, display: "flex", gap: 8, alignItems: "center", lineHeight: 1.12 }}>
                  <span>{heroRecommendation.icon}</span>
                  <span>{heroRecommendation.label}</span>
                </div>
                {parkWeatherSummary ? (
                  <div style={{ position: "relative", marginTop: 8, fontSize: isMobile ? 10 : 11.5, color: "rgba(255,255,255,0.82)", lineHeight: 1.32 }}>
                    {parkWeatherSummary}
                  </div>
                ) : null}
              </>
            ) : (
              <div style={{ position: "relative", marginTop: 6, fontSize: 18, fontWeight: 700, color: "#fff1f2" }}>
                LEGOLAND is closed on {selectedDate || "this date"}. Please pick another date before planning travel.
              </div>
            )}
            {dayError && !dayLoading && (
              <div className="parking-disclaimer" style={{ position: "relative", marginTop: 6, color: dayData?.is_open ? "rgba(255,255,255,0.86)" : "#fff1f2" }}>
                {dayError}
              </div>
            )}
          </div>
        )}

        {!totalDayCosts && (
          <div className="parking-disclaimer" style={{ marginTop: 8 }}>
            No ticket data for this date, so full-day totals are hidden. Travel costs are still modelled above.
          </div>
        )}
        {totalDayCosts && (
          <div
            style={{
              display: "grid",
              gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))",
              gap: isMobile ? 8 : 12,
              marginTop: isMobile ? 12 : 18
            }}
          >
            {transportCards.map((card) => (
              <div
                key={card.key}
                style={{
                  border: `2px solid ${card.borderColor}`,
                  borderRadius: 16,
                  padding: isMobile ? 10 : 16,
                  background: card.accent,
                  boxShadow: card.shadow
                }}
              >
                <div style={{ display: "flex", alignItems: "center", gap: 8, fontWeight: 800, fontSize: isMobile ? 15 : 22, lineHeight: 1.08 }}>
                  <span>{card.icon}</span>
                  <span>{card.label}</span>
                  {card.liveBadge && (
                    <span
                      style={{
                        marginLeft: "auto",
                        fontSize: isMobile ? 9.5 : 11,
                        fontWeight: 700,
                        color: "#0f62b6",
                        background: "#f5fbff",
                        border: "1px solid #9fd5ff",
                        borderRadius: 999,
                        padding: isMobile ? "2px 6px" : "3px 8px",
                        lineHeight: 1
                      }}
                    >
                      {card.liveBadge}
                    </span>
                  )}
                </div>
                <div style={{ marginTop: isMobile ? 6 : 12, fontSize: isMobile ? 10 : 12, textTransform: "uppercase", letterSpacing: 0.6, color: "#64748b", lineHeight: 1.24 }}>
                  {totalCostLabel}
                </div>
                <div style={{ marginTop: 4, fontSize: isMobile ? 19 : 26, fontWeight: 800, lineHeight: 1 }}>{card.total}</div>
                {Array.isArray(card.breakdownItems) && card.breakdownItems.length > 0 ? (
                  <div
                    style={{
                      marginTop: 8,
                      display: "grid",
                      gridTemplateColumns: "repeat(3, minmax(0, 1fr))",
                      gap: isMobile ? 6 : 8,
                      padding: isMobile ? "7px 8px" : "8px 10px",
                      borderRadius: 12,
                      background: "rgba(255,255,255,0.52)",
                      border: "1px solid rgba(148,163,184,0.22)"
                    }}
                  >
                    {card.breakdownItems.map((item) => (
                      <div key={`${card.key}-${item.label}`} style={{ minWidth: 0 }}>
                        <div
                          style={{
                            fontSize: isMobile ? 9 : 10,
                            textTransform: "uppercase",
                            letterSpacing: "0.04em",
                            color: "#64748b",
                            lineHeight: 1.2
                          }}
                        >
                          {item.label}
                        </div>
                        <div
                          style={{
                            marginTop: 2,
                            fontSize: isMobile ? 11.5 : 13,
                            fontWeight: 800,
                            color: "#0f172a",
                            lineHeight: 1.15
                          }}
                        >
                          {item.value}
                        </div>
                      </div>
                    ))}
                  </div>
                ) : null}
                {card.timeline?.stops?.length ? (
                  <div
                    style={{
                      marginTop: 8,
                      padding: isMobile ? "7px 8px" : "8px 10px",
                      minHeight: isMobile ? 64 : 84,
                      borderRadius: 12,
                      background: "rgba(255,255,255,0.58)",
                      border: "1px solid rgba(148,163,184,0.25)"
                    }}
                  >
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${card.timeline.stops.length}, minmax(0, 1fr))`,
                        gap: isMobile ? 6 : 8,
                        fontSize: isMobile ? 10.5 : 11.5,
                        fontWeight: 800,
                        color: "#0f172a"
                      }}
                    >
                      {card.timeline.stops.map((stop, idx) => (
                        <div key={`${card.key}-time-${idx}`}>{stop.time}</div>
                      ))}
                    </div>
                    <div
                      style={{
                        display: "grid",
                        gridTemplateColumns: `repeat(${card.timeline.stops.length}, minmax(0, 1fr))`,
                        gap: isMobile ? 6 : 8,
                        marginTop: 2,
                        minHeight: isMobile ? 24 : 34,
                        alignItems: "start",
                        fontSize: isMobile ? 9.5 : 10.5,
                        color: "#475467",
                        lineHeight: 1.25
                      }}
                    >
                      {card.timeline.stops.map((stop, idx) => (
                        <div key={`${card.key}-label-${idx}`}>{stop.label}</div>
                      ))}
                    </div>
                  </div>
                ) : null}
                <div style={{ marginTop: isMobile ? 6 : 12, fontSize: isMobile ? 11.5 : 14, color: "#334155", lineHeight: 1.24 }}>Duration: {card.duration}</div>
                <div style={{ marginTop: 4, fontSize: isMobile ? 11.5 : 14, color: "#334155", lineHeight: 1.28 }}>{card.meta}</div>
                {card.localLinkLabel ? (
                  <button
                    type="button"
                    onClick={openLocalTransportDetails}
                    style={{
                      marginTop: 7,
                      padding: 0,
                      border: "none",
                      background: "transparent",
                      color: "#0f62b6",
                      fontSize: isMobile ? 10.5 : 12,
                      fontWeight: 700,
                      textAlign: "left",
                      cursor: "pointer"
                    }}
                  >
                    {card.localLinkLabel}
                  </button>
                ) : null}
                {heroRecommendation?.key === card.key && (
                  <div style={{ marginTop: 8, fontSize: isMobile ? 10 : 12, fontWeight: 700, color: heroRecommendation?.key === "drive" ? "#9a3412" : heroRecommendation?.key === "public" ? "#0f62b6" : "#b42318" }}>
                    Recommended for this date
                  </div>
                )}
              </div>
            ))}
          </div>
        )}

        <LegolandLiveSection />

        <details className="train-collapsible" style={{ marginTop: 14 }}>
          <summary className="train-summary-bar" style={isMobile ? { padding: "10px 12px", fontSize: 14.5, lineHeight: 1.2 } : undefined}>Detailed cost breakdown</summary>
          <div className="train-collapsible-body">
            {totalDayCosts ? (
              modesToShow.map(renderOption)
            ) : (
              <div className="parking-disclaimer" style={{ marginTop: 8 }}>
                Ticket data missing for this date, so transport option breakdown is hidden.
              </div>
            )}
          </div>
        </details>
        <details className="train-collapsible">
          <summary className="train-summary-bar" style={isMobile ? { padding: "10px 12px", fontSize: 14.5, lineHeight: 1.2 } : undefined}>🧭 Time, walking, and hassle</summary>
          <div className="train-collapsible-body">
            <div className="leave-chips leave-chips-grid">
              <div className="leave-chip">
                <div className="leave-chip-label">Drive</div>
                <div className="leave-chip-value">{transportEstimates.drive.duration} mins • {transportEstimates.drive.walkMins} mins walk</div>
                <div className="leave-chip-sub">{transportEstimates.drive.hassle.label}</div>
              </div>
              <div className="leave-chip">
                <div className="leave-chip-label">Taxi</div>
                <div className="leave-chip-value">{transportEstimates.taxi.duration} mins • {transportEstimates.taxi.walkMins} mins walk</div>
                <div className="leave-chip-sub">{transportEstimates.taxi.hassle.label}</div>
              </div>
              <div className="leave-chip">
                <div className="leave-chip-label">Public transport</div>
                <div className="leave-chip-value">{transportEstimates.public.duration} mins • {transportEstimates.public.walkMins} mins walk • {changeCountText(transportEstimates.public.changes)}</div>
                <div className="leave-chip-sub">{transportEstimates.public.hassle.label}</div>
              </div>
            </div>
          </div>
        </details>

        <details className="train-collapsible">
          <summary className="train-summary-bar" style={isMobile ? { padding: "10px 12px", fontSize: 14.5, lineHeight: 1.2 } : undefined}>✅ What this planner includes</summary>
          <div className="train-collapsible-body">
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.55 }}>
              <li>Live taxi pricing where CheapCabGrab preview fares are available for your search.</li>
              <li>Live National Rail fares where the rail planner returns a live route.</li>
              <li>Real LEGOLAND parking prices, including standard, priority, and disabled parking.</li>
              <li>Live opening days and hours for the selected date, plus current ticket from-prices.</li>
              <li>Ticket pricing that respects adults, paying children, and under-90cm free entry rules.</li>
              <li>Door-to-door timing with a compact leave-home strip for drive, taxi, and train.</li>
              <li>Rail routing that explains first mile to the station, then the train, then the last mile to LEGOLAND.</li>
              <li>Walking time, changes, and a hassle score to show effort, not just cost.</li>
              <li>Opening-time weather forecast for your visit when forecast data is available.</li>
            </ul>
            <div className="parking-disclaimer" style={{ marginTop: 8 }}>
              For LEGOLAND rail journeys, this planner usually prefers Slough or Windsor station routes and often uses a last-mile taxi because it is clearer and lower-hassle than relying on bus or coach variants for the final stretch.
            </div>
          </div>
        </details>

        <details
          className="train-collapsible"
          ref={localTransportRef}
          open={localTransportOpen}
          onToggle={(e) => setLocalTransportOpen(e.currentTarget.open)}
        >
          <summary className="train-summary-bar" style={isMobile ? { padding: "10px 12px", fontSize: 14.5, lineHeight: 1.2 } : undefined}>
            🚌 Slough, Heathrow and nearby hotel transport to LEGOLAND
          </summary>
          <div className="train-collapsible-body">
            <div className="parking-disclaimer" style={{ marginBottom: 10, lineHeight: 1.45 }}>
              These are the most practical last-mile and nearby options we see people worry about most: Slough Station, Heathrow Terminal 5, Windsor, and the main Slough hotels. We still prefer taxi for the clearest last mile, but the 702 / 703 buses can be much cheaper.
            </div>

            <div style={{ fontWeight: 800, fontSize: isMobile ? 14 : 15, marginBottom: 8 }}>
              Bus 702 & 703 to LEGOLAND
            </div>
            {isMobile ? (
              <div style={{ display: "grid", gap: 10 }}>
                {LEGOLAND_BUS_ROWS.map((row) => (
                  <div key={row.from} style={{ border: "1px solid #e5edf6", borderRadius: 14, padding: "10px 12px", background: "#fff" }}>
                    <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>{row.from}</div>
                    <div style={{ fontSize: 12, color: "#475467", marginTop: 4 }}>{row.bus} to {row.to}</div>
                    <div style={{ marginTop: 6, fontSize: 11.5, color: "#334155", lineHeight: 1.45 }}>
                      <div><strong>From stop:</strong> {row.fromStop}</div>
                      <div><strong>To stop:</strong> {row.toStop}</div>
                      <div><strong>Duration:</strong> {row.duration}</div>
                      <div><strong>Frequency:</strong> {row.frequency}</div>
                      <div><strong>Fare:</strong> {row.fare}</div>
                    </div>
                  </div>
                ))}
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, lineHeight: 1.4 }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "#0f172a", borderBottom: "1px solid #dbe7f2" }}>
                      <th style={{ padding: "8px 8px 10px 0" }}>From</th>
                      <th style={{ padding: "8px 8px 10px 0" }}>Bus</th>
                      <th style={{ padding: "8px 8px 10px 0" }}>From stop</th>
                      <th style={{ padding: "8px 8px 10px 0" }}>To stop</th>
                      <th style={{ padding: "8px 8px 10px 0" }}>Duration</th>
                      <th style={{ padding: "8px 8px 10px 0" }}>Frequency</th>
                      <th style={{ padding: "8px 0 10px 0" }}>Fare</th>
                    </tr>
                  </thead>
                  <tbody>
                    {LEGOLAND_BUS_ROWS.map((row) => (
                      <tr key={row.from} style={{ borderBottom: "1px solid #eef2f7", verticalAlign: "top" }}>
                        <td style={{ padding: "10px 8px 10px 0", fontWeight: 700, color: "#0f172a" }}>{row.from}</td>
                        <td style={{ padding: "10px 8px 10px 0" }}>{row.bus}</td>
                        <td style={{ padding: "10px 8px 10px 0" }}>{row.fromStop}</td>
                        <td style={{ padding: "10px 8px 10px 0" }}>{row.toStop}</td>
                        <td style={{ padding: "10px 8px 10px 0" }}>{row.duration}</td>
                        <td style={{ padding: "10px 8px 10px 0" }}>{row.frequency}</td>
                        <td style={{ padding: "10px 0 10px 0", fontWeight: 700 }}>{row.fare}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ fontWeight: 800, fontSize: isMobile ? 14 : 15, marginTop: 14, marginBottom: 8 }}>
              Live taxi fares from nearby points
            </div>
            {localTaxiLoading ? (
              <div className="parking-disclaimer">Checking live taxi preview fares for nearby starting points…</div>
            ) : isMobile ? (
              <div style={{ display: "grid", gap: 10 }}>
                {LEGOLAND_LOCAL_TAXI_POINTS.map((point) => {
                  const quote = localTaxiQuotes[point.id];
                  return (
                    <div key={point.id} style={{ border: "1px solid #e5edf6", borderRadius: 14, padding: "10px 12px", background: "#fff" }}>
                      <div style={{ fontSize: 13, fontWeight: 800, color: "#0f172a" }}>{point.label}</div>
                      <div style={{ marginTop: 6, fontSize: 11.5, color: "#334155", lineHeight: 1.45 }}>
                        <div><strong>To:</strong> LEGOLAND</div>
                        <div><strong>Live taxi fare:</strong> {quote?.fare != null ? `${money(quote.fare, { decimals: 2 })} (saloon)` : "Check on demand"}</div>
                        <div><strong>Typical time:</strong> {point.duration}</div>
                        <div><strong>Notes:</strong> {point.note}</div>
                      </div>
                      <button
                        type="button"
                        onClick={() => openNearbyTaxiQuote(point)}
                        disabled={localTaxiBookingId === point.id}
                        style={{
                          marginTop: 8,
                          padding: "8px 12px",
                          borderRadius: 999,
                          border: "1px solid #fbbf24",
                          background: "#facc15",
                          color: "#111827",
                          fontSize: 12,
                          fontWeight: 800,
                          cursor: "pointer"
                        }}
                      >
                        {localTaxiBookingId === point.id ? "Opening…" : quote?.fare != null ? `${money(quote.fare, { decimals: 2 })} Book now` : "Get quote"}
                      </button>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, lineHeight: 1.4 }}>
                  <thead>
                    <tr style={{ textAlign: "left", color: "#0f172a", borderBottom: "1px solid #dbe7f2" }}>
                      <th style={{ padding: "8px 8px 10px 0" }}>From</th>
                      <th style={{ padding: "8px 8px 10px 0" }}>To</th>
                      <th style={{ padding: "8px 8px 10px 0" }}>Typical time</th>
                      <th style={{ padding: "8px 8px 10px 0" }}>Notes</th>
                      <th style={{ padding: "8px 0 10px 12px", textAlign: "right" }}>Fare / action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {LEGOLAND_LOCAL_TAXI_POINTS.map((point) => {
                      const quote = localTaxiQuotes[point.id];
                      return (
                        <tr key={point.id} style={{ borderBottom: "1px solid #eef2f7", verticalAlign: "top" }}>
                          <td style={{ padding: "10px 8px 10px 0", fontWeight: 700, color: "#0f172a" }}>{point.label}</td>
                          <td style={{ padding: "10px 8px 10px 0" }}>LEGOLAND</td>
                          <td style={{ padding: "10px 8px 10px 0" }}>{point.duration}</td>
                          <td style={{ padding: "10px 8px 10px 0" }}>{point.note}</td>
                          <td style={{ padding: "10px 0 10px 12px", textAlign: "right", whiteSpace: "nowrap" }}>
                            <button
                              type="button"
                              onClick={() => openNearbyTaxiQuote(point)}
                              disabled={localTaxiBookingId === point.id}
                              style={{
                                padding: "7px 12px",
                                borderRadius: 999,
                                border: "1px solid #fbbf24",
                                background: "#facc15",
                                color: "#111827",
                                fontSize: 12,
                                fontWeight: 800,
                                cursor: "pointer"
                              }}
                            >
                              {localTaxiBookingId === point.id ? "Opening…" : quote?.fare != null ? `${money(quote.fare, { decimals: 2 })} Book now` : "Get quote"}
                            </button>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}

            <div style={{ fontWeight: 800, fontSize: isMobile ? 14 : 15, marginTop: 14, marginBottom: 8 }}>
              Practical notes
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.55 }}>
              <li>Landmark Place (Stop S) is the main Slough boarding stop and the easiest one for most hotel stays.</li>
              <li>The buses stop at LEGOLAND Park &amp; Ride (Stop LP), then it is roughly a 5 minute walk to the entrance.</li>
              <li>From Heathrow Terminals 2–3, the simplest public transport route is usually Elizabeth Line to Slough, then the 702 / 703 bus, or transfer to Terminal 5 for the direct 703.</li>
              <li>Bus fares above are static guidance. Taxi fares above use live CheapCabGrab preview pricing when available.</li>
              <li>Timetables, fares, and hotel access routes can change, so always allow extra time on busy dates.</li>
            </ul>
          </div>
        </details>

        <details className="train-collapsible">
          <summary className="train-summary-bar" style={isMobile ? { padding: "10px 12px", fontSize: 14.5, lineHeight: 1.2 } : undefined}>
            ❓ Is 90cm strict, which rides queue first, and what is the buffet price? See first-timer FAQs
          </summary>
          <div className="train-collapsible-body">
            <div style={{ display: "grid", gap: 12 }}>
              {[
                {
                  q: "Can I use my parking booking if I bring a different car?",
                  a: "Yes. Your barcode should still work at the barrier even if the registration is different or was not entered. The system may use number plate details for convenience, but entry is mainly via barcode, so adding your registration can help with a faster exit but is not mandatory for entry."
                },
                {
                  q: "What are the main ways to get discounted LEGOLAND tickets?",
                  a: "The most common options people check are advance online booking, Sky VIP, Tesco Clubcard, Blue Light or NHS discounts, and Kellogg's or Cadbury promotions. Sky VIP deals are often around £18 to £20, while cereal or chocolate offers are usually based on full gate prices, so the real saving can vary a lot by date."
                },
                {
                  q: "Is under 90cm free, and what if my child is 97cm?",
                  a: "Children under 90cm usually enter free. At 97cm, you should expect to need a paid ticket. Staff can be strict on height checks, so it is safer to book in advance rather than risk the on-the-day gate price."
                },
                {
                  q: "What age is LEGOLAND best for?",
                  a: "LEGOLAND is usually strongest for younger children, especially roughly ages 2 to 10. Ages 7 to 11 often get the best mix of independence and ride access without needing the more intense coaster experience of thrill parks."
                },
                {
                  q: "Which rides should we do first?",
                  a: "Popular first picks are Flight of the Sky Lion, NINJAGO, The Dragon, Haunted House and Laser Raiders. If you have early entry or arrive at opening, head to the big queue-builders first."
                },
                {
                  q: "Are queues okay midweek?",
                  a: "Midweek term-time visits are usually much easier than weekends. Families often report many short queues midweek, while Saturdays and school-holiday weekends can quickly move into 20 to 50 minute waits on headline rides."
                },
                {
                  q: "Do we need fast track at weekends?",
                  a: "Not always, but it is much more worth considering for busy Saturdays, school holidays, and big event weekends than for a quiet midweek day."
                },
                {
                  q: "Is one day enough?",
                  a: "One day can work if queues are light and you arrive early, but it can still feel tight with younger children because there is a lot of walking, snack stops, and ride pacing. Two days is more relaxed if you want hotels and evening extras too."
                },
                {
                  q: "What is the Merlin buffet price?",
                  a: `The Pizza & Pasta buffet with a Merlin Pass is typically ${money(LEGOLAND_BUFFET_MERLIN.adult, { decimals: 2 })} for adults and ${money(LEGOLAND_BUFFET_MERLIN.child)} for children, usually with a refill soft drink included.`
                },
                {
                  q: "How much is the Driving School licence souvenir?",
                  a: "The photo driving licence souvenir is usually about £11.99 online including VAT and around £13.99 in the park. Kids aged 6–13 who are roughly 1.1m to 1.5m can drive the cars on the ride itself, but the printed photo licence is an extra paid souvenir."
                },
                {
                  q: "Should we bring our own food?",
                  a: "If budget matters, yes. In-park meals can add up quickly, while the Merlin buffet can be a good value middle ground if you already have the right pass."
                },
                {
                  q: "Are the LEGOLAND hotels worth it?",
                  a: "They are mainly worth it for convenience, themed rooms, and the kid-focused experience rather than value. Resort Hotel is usually the strongest all-rounder, while Woodland Village works well in warmer months."
                },
                {
                  q: "What evening things do the hotels usually have?",
                  a: "Guests often ask about meet-and-greets, bingo, disco, play areas, mini golf, and swimming. Swimming and some dining slots usually need booking, and evening entertainment can differ between Resort, Castle, and Woodland stays."
                },
                {
                  q: "What is the easiest way from Slough or Heathrow to LEGOLAND?",
                  a: "From Slough, taxi is usually the simplest last mile, but the 702 / 703 buses are much cheaper. From Heathrow Terminal 5, the 703 is the direct budget option, while a taxi is usually faster and easier with kids or luggage."
                },
              ].map((item) => (
                <div key={item.q} style={{ borderBottom: "1px solid #eef2f7", paddingBottom: 10 }}>
                  <div style={{ fontWeight: 800, color: "#0f172a", fontSize: isMobile ? 13.5 : 14.5, lineHeight: 1.35 }}>
                    {item.q}
                  </div>
                  <div style={{ marginTop: 4, color: "#334155", fontSize: isMobile ? 12 : 13.5, lineHeight: 1.55 }}>
                    {item.a}
                  </div>
                </div>
              ))}
            </div>
            <div className="parking-disclaimer" style={{ marginTop: 10 }}>
              Queue patterns, hotel activities, buffet offers, and transport details can all change by date and season. Treat this as practical planning guidance and always verify key details before you travel.
            </div>
          </div>
        </details>

        <details className="train-collapsible">
          <summary className="train-summary-bar" style={isMobile ? { padding: "10px 12px", fontSize: 14.5, lineHeight: 1.2 } : undefined}>🍔 Food options & Merlin buffet</summary>
          <div className="train-collapsible-body">
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.55 }}>
              <li>Pizza & Pasta buffet with a Merlin Pass is typically {money(LEGOLAND_BUFFET_MERLIN.adult, { decimals: 2 })} per adult and {money(LEGOLAND_BUFFET_MERLIN.child)} per child.</li>
              <li>The Merlin buffet is commonly available at Beginning Coffee Co. and City Walk Pizza & Pasta.</li>
              <li>A refill soft drink is usually included with the Merlin buffet upgrade.</li>
              <li>If you pick `Buffet (Merlin)` in the planner, food totals switch from estimate to an exact buffet total for adults and paying children.</li>
            </ul>
            <div className="parking-disclaimer" style={{ marginTop: 8 }}>
              Buffet locations, offers, and prices can change. Always verify on the day, especially for seasonal menus, Merlin pass offers, and refill drink inclusions.
            </div>
          </div>
        </details>

        <details className="train-collapsible">
          <summary className="train-summary-bar" style={isMobile ? { padding: "10px 12px", fontSize: 14.5, lineHeight: 1.2 } : undefined}>🏨 On-site hotels: simple comparison</summary>
          <div className="train-collapsible-body">
            <div style={{ color: "#334155", lineHeight: 1.6, marginBottom: 12 }}>
              Most of the checklist is the same across all on-site stays. The real choice is usually convenience and LEGO theming versus a cheaper, quieter Woodland stay.
            </div>

            <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "repeat(3, minmax(0, 1fr))", gap: 12, marginBottom: 14 }}>
              {LEGOLAND_ONSITE_HOTELS.map((hotel) => (
                <div key={hotel.id} style={{ border: "1px solid #dbe7f2", borderRadius: 14, padding: 12, background: "#fff" }}>
                  <div style={{ fontSize: 12, fontWeight: 800, color: "#b45309", textTransform: "uppercase", letterSpacing: 0.4 }}>
                    {hotel.badge}
                  </div>
                  <div style={{ fontSize: 16, fontWeight: 900, color: "#0f172a", marginTop: 4 }}>
                    {hotel.name}
                  </div>
                  <div style={{ fontSize: 13, color: "#334155", marginTop: 6, lineHeight: 1.5 }}>
                    {hotel.vibe}
                  </div>
                  <div style={{ marginTop: 10, display: "grid", gap: 6, fontSize: 12.5, color: "#334155" }}>
                    <div><strong>Walk:</strong> {hotel.walk}</div>
                    <div><strong>Rooms:</strong> {hotel.roomStyle}</div>
                    <div><strong>Bathroom:</strong> {hotel.bathroom}</div>
                    <div><strong>Breakfast:</strong> {hotel.breakfast}</div>
                    <div><strong>Parking:</strong> {hotel.parking}</div>
                    <div><strong>Highlights:</strong> {hotel.entertainment}</div>
                    <div><strong>Note:</strong> {hotel.extras}</div>
                  </div>
                  <div style={{ marginTop: 10, paddingTop: 10, borderTop: "1px solid #eef2f7", fontSize: 13 }}>
                    <strong>Typical 1-night package:</strong> {money(hotel.package)} for 2 adults + 1 child
                  </div>
                </div>
              ))}
            </div>

            <div style={{ overflowX: "auto" }}>
              <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5, lineHeight: 1.45 }}>
                <thead>
                  <tr style={{ textAlign: "left", color: "#0f172a", borderBottom: "1px solid #dbe7f2" }}>
                    <th style={{ padding: "8px 10px 10px 0" }}>What actually matters</th>
                    <th style={{ padding: "8px 10px 10px 0" }}>Resort Hotel</th>
                    <th style={{ padding: "8px 10px 10px 0" }}>Castle Hotel</th>
                    <th style={{ padding: "8px 0 10px 0" }}>Woodland Village</th>
                  </tr>
                </thead>
                <tbody>
                  {LEGOLAND_HOTEL_COMPARISON_ROWS.map((row) => (
                    <tr key={row.label} style={{ borderBottom: "1px solid #eef2f7", verticalAlign: "top" }}>
                      <td style={{ padding: "9px 10px 9px 0", fontWeight: 800, color: "#0f172a", minWidth: 140 }}>{row.label}</td>
                      <td style={{ padding: "9px 10px 9px 0" }}>{row.resort}</td>
                      <td style={{ padding: "9px 10px 9px 0" }}>{row.castle}</td>
                      <td style={{ padding: "9px 0 9px 0" }}>{row.woodland}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div style={{ fontWeight: 800, fontSize: isMobile ? 14 : 15, marginTop: 14, marginBottom: 8 }}>
              Honest take
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.55 }}>
              <li>Most room basics are shared: bed linen, towels, toiletries, TV, tea and coffee, mini fridge, hair dryer, safe, heating, WiFi, parking, and usually early ride access if tickets are included.</li>
              <li>The biggest real differences are walk time, how immersive the stay feels, and whether you are happy with Woodland’s less hotel-like setup.</li>
              <li>If you want the full LEGO experience, pick Resort Hotel. If you want a similar stay with a calmer premium feel, pick Castle. If budget matters more, Woodland is the clear value option.</li>
            </ul>

            <div className="parking-disclaimer" style={{ marginTop: 8 }}>
              Typical package prices above are a practical guide for 2 adults and 1 child with 1-day ticket, 1-night stay, breakfast, free parking, and early access style perks. Room type, date, and school holiday demand can move prices a lot.
            </div>
          </div>
        </details>

        <details className="train-collapsible">
          <summary className="train-summary-bar" style={isMobile ? { padding: "10px 12px", fontSize: 14.5, lineHeight: 1.2 } : undefined}>ℹ️ Parking, EV charging and park detail sources</summary>
          <div className="train-collapsible-body">
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              <li>Standard parking: {money(parkingPrices.standard)} ({isWinter ? "winter" : "normal"} rate)</li>
              <li>Priority parking (closer): {money(parkingPrices.priority)} — required for EV charging</li>
              <li>Disabled parking: {money(parkingPrices.disabled)} (subject to availability)</li>
              <li>Taxi / drop-off: 30 minutes free, no parking charge if driver leaves</li>
              <li>Exit congestion: cars queue at closing — taxis avoid this</li>
              <li>⚡ EV: 34 charge points in Priority Car Park; pay via ChargePoint</li>
              <li>Price source: {dayData?.price_source_url || "—"}</li>
              <li>
                Price checked: {dayData?.price_retrieved_at ? new Date(dayData.price_retrieved_at).toLocaleString() : "—"}
              </li>
              <li>
                Calendar checked: {dayData?.calendar_verified_at ? new Date(dayData.calendar_verified_at).toLocaleString() : "—"}
              </li>
              <li>Calendar source: {dayData?.calendar_source_url || "—"}</li>
              {dayData?.event_label && <li>Event: {dayData.event_label}</li>}
              {monthError && <li style={{ color: "#b45309" }}>{monthError}</li>}
            </ul>
          </div>
        </details>

        <details className="train-collapsible">
          <summary className="train-summary-bar" style={isMobile ? { padding: "10px 12px", fontSize: 14.5, lineHeight: 1.2 } : undefined}>🚦 Travel mode rules and smart recommendations</summary>
          <div className="train-collapsible-body">
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              <li>Train: live National Rail routing usually reaches Slough or Windsor first, then adds first-mile station access and a last-mile taxi to LEGOLAND.</li>
              <li>Bus/coach: coach and Green Line options exist, but this planner currently scores the rail-based route rather than coach timetables.</li>
              <li>Taxi: Designated drop-off with 30 min free; best for pushchairs, tired kids, bad weather, and late departures.</li>
              <li>Drive: Parking queues peak at opening/closing; 9-minute walk from car parks to gates.</li>
            </ul>
            <div className="parking-disclaimer" style={{ marginTop: 6 }}>
              Hassle scores above reflect first-mile access and station changes for rail, parking queues for cars, and minimal walking for taxis.
            </div>
            <ul style={{ margin: 0, paddingLeft: 18, lineHeight: 1.5 }}>
              {recommendation.map((r, idx) => (
                <li key={idx}>{r}</li>
              ))}
            </ul>
          </div>
        </details>

        <div className="parking-disclaimer" style={{ marginTop: 12 }}>
          Data sources: ticket prices based on advance online listings and current price snapshots; parking rates per LEGOLAND published winter/standard pricing; taxi pricing uses live CheapCabGrab preview fares where available; public transport uses live National Rail fares where available, with modelled fallback estimates if live rail is unavailable. Always verify on the day. Updated: 12th May 2026.
        </div>
      </div>

      <div style={{ marginTop: 20, border: "1px solid #e5e7eb", borderRadius: 12, padding: 12, background: "#fff" }}>
        <strong>Plan your Legoland day:</strong>
        <ul style={{ marginTop: 8, lineHeight: 1.6, marginBottom: 0 }}>
          <li>✈️ <a href="/legoland-heathrow">Heathrow terminal transport to LEGOLAND</a> — compare Heathrow taxi, rail, and bus options</li>
          <li>🚕 <a href="/slough-taxi">Slough taxi</a> — quickest local pickup</li>
          <li>🚕 <a href="/windsor-taxi">Windsor taxi</a> — station to park drop-off</li>
          <li>🚕 <a href="/maidenhead-taxi">Maidenhead taxi</a></li>
          <li>✈️ <a href="/east-midlands-airport/live-journey-planner">Live East Midlands Airport journey planner</a> — compare taxi, rail, and parking with live options</li>
          <li>🏰 <a href="/windsor-castle-budget-planner">Windsor Castle Budget Planner</a> — compare Castle tickets, taxi, rail, parking and weather</li>
          <li>🆚 <a href="/legoland-vs-thorpe-park">LEGOLAND vs Thorpe Park (compare)</a></li>
          <li>🎢 <a href="/thorpepark-budget-planner">Thorpe Park Day Planner</a></li>
          <li>🚆 <a href="/train-vs-taxi">Train vs Taxi (national)</a></li>
          <li>ℹ️ <a href="https://www.legoland.co.uk" target="_blank" rel="noreferrer">Legoland official site</a></li>
          <li>🚇 <a href="https://tfl.gov.uk" target="_blank" rel="noreferrer">TfL journey planner</a></li>
          <li>🚆 <a href="https://www.nationalrail.co.uk" target="_blank" rel="noreferrer">National Rail</a></li>
        </ul>
      </div>

      <div style={{ marginTop: "40px" }}>
        <ReviewsBar />
      </div>
    </div>
  );
}
